import UseContext from 'hooks/useContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { isSuperAdmin } from 'helper/roles';
import PanelCard, { cardIconButtonClass } from 'components/card/PanelCard';
import {
  contractGenerationDate,
  contractGenerationLabel,
  isContractGenerationDue,
} from 'helper/contractGeneration';
import {
  BellIcon,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronTop,
  Close,
  CrossIcon,
  DocumentIcon,
  EyeIcon,
  GroupPeople,
  LinkIcon,
  Pencil,
  Plus,
  ProfilIcon,
  RefreshIcon,
  SearchLoop,
  Signer,
  TrashIcon,
  Warning,
} from '../../../assets/icons';
import AddressGeoApi from '../../../components/AddressGeoApi/AddressGeoApi';
import useSlideOver from '../../../components/SlideOver/useSlideOver';
import Badge from '../../../components/badge/badge';
import BadgeSquare from '../../../components/badge/badgeSquare';
import Banner from '../../../components/banner/banner';
import Button from '../../../components/button';
import CopyToClipboard from '../../../components/copyToClipboard';
import Tooltip from '../../../components/tooltips/Tooltips';
import Divider from '../../../components/divider/divider';
import IdentityFileItem from '../../../components/identityFileItem/IdentityFileItem';
import useModal from '../../../components/modal/useModal';
import Pagination from '../../../components/pagination/pagination';
import Salary from '../../../components/salary/Salary';
import Schedule from '../../../components/schedule/schedule';
import StyledInput from '../../../components/styledInput/StyledInput';
import TempWorkerMission from '../../../components/tempWorkerMission/tempWorkerMission';
import PostIt from '../../../components/postIt/PostIt';
import {
  dateToEpoch,
  dateToIsoString,
  dateWordShort,
  formatDateCreation,
} from '../../../helper/helper';
import usefetchdata from '../../../hooks/usefetchdata';
import ApiDatabase from '../../../server';
import MissionDetailTempWorker from './missionDetailTempWorker';
import MissionActivityTab from './missionActivityTab';

// Icone triangle pour demande en attente
function TrianglePendingIcon({ wh = 22, color = '#D77A1E' }) {
  return (
    <svg width={wh} height={wh} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.9089 17.2727L19.0907 19.0909L10.9089 2.72729L2.72705 19.0909L10.9089 17.2727ZM10.9089 17.2727V10" stroke={color} strokeWidth="1.81818" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function missionStatusReasonLabel(reason) {
  const t = (reason ?? '').trim();
  if (!t || t === '—' || t === '-') return '(Voir Activité)';
  return t;
}

const AMENDMENT_STATUS_MAP = {
  accepted: { type: '10', label: 'Accepté',   iconBg: 'bg-green-100',  iconColor: '#16A34A' },
  refused:  { type: '3',  label: 'Refusée',   iconBg: 'bg-red-100',    iconColor: '#DC2626' },
  replaced: { type: '18', label: 'Remplacée', iconBg: 'bg-gray-100',   iconColor: '#6B7280' },
  pending:  { type: '20', label: 'En attente', iconBg: 'bg-orange-100', iconColor: '#EA580C' },
  // An admin withdrew the request they sent (JBWEB-1209). Kept on screen like
  // a refusal: the temp worker may already have seen it come in.
  cancelled: { type: '18', label: 'Annulée',   iconBg: 'bg-gray-100',   iconColor: '#6B7280' },
};

const CHANGE_FIELD_LABELS = {
  period: 'Période',
  qualification: 'Qualification',
  salary: 'Salaire',
  schedule: 'Horaires',
  address: 'Adresse',
  reason: 'Motif de recours',
};

const countUsersInCategorizedLists = (lists) =>
  lists && typeof lists === 'object'
    ? Object.values(lists).reduce((sum, users) => sum + (users?.length || 0), 0)
    : 0;

/**
 * The mission created to replace this one.
 *
 * It used to be written inside the "interrupted" block alone, so a mission
 * duplicated without being interrupted — « Nouvelle mission requise » — named
 * its replacement nowhere.
 */
const ReplacementMissionLink = ({ replacement, onOpen }) => (
  <span
    className="font-semibold text-yellow-600 cursor-pointer hover:underline"
    onClick={() => onOpen(replacement._id)}
  >
    Mission n°{replacement.id_mission}
  </span>
);

function ContractsAndAmendmentsList({
  contracts,
  companyContracts,
  amendments,
  canActOnAmendments,
  onAmendmentAction,
  contractHistories,
  missionId,
  hasUserAssigned,
  loadingContractGeneration,
  loadingSignatureMissionId,
  generationContractSubmit,
  regenerateAmendmentSubmit,
  updateContractSubmit,
  sendSignatureMissionSubmit,
  deletedContractHistory,
  onPreviewContract,
  /** The company note, which the design puts under the title of this section. */
  postIt,
  /** The mission itself: the card announces when its contract is expected. */
  mission,
  onGenerateContract,
}) {
  // Fusionner contrats et demandes dans une seule liste ordonnée
  const mergedItems = useMemo(() => {
    const items = [];

    // IDs des demandes déjà liées à un contrat
    const linkedAmendmentIds = new Set();

    // Pour chaque contrat/avenant existant
    contracts.forEach((contract) => {
      const avenantNum = contract.avenant_number || 0;
      // Chercher une demande liée via amendmentRequestId sur le contrat
      const relatedAmendment = amendments.find(
        (a) => contract.amendmentRequestId && a._id === contract.amendmentRequestId
      );

      if (relatedAmendment) {
        linkedAmendmentIds.add(relatedAmendment._id);
      }

      // Si une demande liée existe et n'est PAS acceptée, on affiche la demande (pas l'avenant)
      if (relatedAmendment && relatedAmendment.status !== 'accepted') {
        items.push({
          type: 'amendment_request',
          avenantNumber: avenantNum,
          data: relatedAmendment,
          sortKey: avenantNum,
        });
      } else {
        const companyContract = companyContracts.find(
          (c) => (c.avenant_number || 0) === avenantNum
        );
        items.push({
          type: 'contract',
          avenantNumber: avenantNum,
          data: contract,
          companyContract,
          sortKey: avenantNum,
        });
      }
    });

    // Ajouter les demandes sans contrat correspondant (en attente de génération)
    amendments
      .filter((a) => {
        // Demande non liée à un contrat et non acceptée
        return !linkedAmendmentIds.has(a._id) && a.status !== 'accepted';
      })
      .forEach((amendment) => {
        const avenantNum = amendment.avenantNumber || amendment.avenant_number || 0;
        items.push({
          type: 'amendment_request',
          avenantNumber: avenantNum,
          data: amendment,
          sortKey: avenantNum,
        });
      });

    // Trier par avenant_number décroissant (plus récent en premier)
    // En cas d'égalité, trier par date de création décroissante (demandes plus récentes d'abord)
    return items.sort((a, b) => {
      if (b.sortKey !== a.sortKey) {
        return b.sortKey - a.sortKey;
      }
      // Même avenant_number : trier par date de création
      const dateA = new Date(a.data.createdAt || 0).getTime();
      const dateB = new Date(b.data.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [contracts, companyContracts, amendments]);

  const hasGeneratedContracts = contracts.some((c) => c.id_bestt);
  const showEmptyState = contracts.length === 0 || !hasGeneratedContracts;

  // JBWEB-1138: nothing generates a contract but an admin asking for one, so
  // the card says when that is expected rather than leaving it to be guessed.
  const generationLabel = contractGenerationLabel(contractGenerationDate(mission));
  const isGenerationDue = isContractGenerationDue(mission);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
      <div className="text-sm font-semibold text-gray-900">Contrats & Avenants</div>
      {postIt}

      {showEmptyState && (
        <PanelCard
          icon={<Signer wh={20} color="#374151" />}
          // Dashed, as the design draws what is not there yet.
          iconClassName="border border-dashed border-gray-300"
          title="Contrat non généré"
          titleClassName="text-sm font-semibold"
          subtitle={
            generationLabel
              ? `Le contrat pourra être généré le ${generationLabel}`
              : 'Aucun contrat BeSTT généré'
          }
          badge={
            loadingContractGeneration ? (
              <Button size="SM" color="DISABLED">
                Génération ...
              </Button>
            ) : (
              // Primary once the mission is under way: generating is then what
              // is expected. Before that it is a forcing, and the modal behind
              // it says which of the two this is.
              <Button
                size="SM"
                color={isGenerationDue ? 'PRIMARY' : 'SECONDARY'}
                onClick={onGenerateContract}
              >
                Générer
              </Button>
            )
          }
        />
      )}

      {mergedItems.map((item, idx) => {
        const isLatest = idx === 0;

        if (item.type === 'amendment_request') {
          return (
            <AmendmentRequestItem
              key={`amendment-${item.data._id}`}
              request={item.data}
              canAct={canActOnAmendments}
              onAction={onAmendmentAction}
            />
          );
        }

        const isAmendment = item.avenantNumber > 0;
        return (
          <ContractCardItem
            key={`contract-${item.data._id}`}
            contract={item.data}
            companyContract={item.companyContract}
            isLatest={isLatest}
            isAmendment={isAmendment}
            missionId={missionId}
            idx={idx}
            hasUserAssigned={hasUserAssigned}
            onPreview={onPreviewContract}
            loadingContractGeneration={loadingContractGeneration}
            loadingSignatureMissionId={loadingSignatureMissionId}
            generationContractSubmit={generationContractSubmit}
            regenerateAmendmentSubmit={regenerateAmendmentSubmit}
            updateContractSubmit={updateContractSubmit}
            sendSignatureMissionSubmit={sendSignatureMissionSubmit}
          />
        );
      })}

      </div>

      {contractHistories.filter((c) => c.status === 'toDelete').length > 0 && (
        <>
          <Divider />
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-sm font-semibold text-gray-900">Historique des contrats / avenants</div>
              <div className="text-sm text-gray-500">
                Vous devez manuellement supprimer ces éléments sur BeSTT.
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {contractHistories
                .filter((c) => c.status === 'toDelete')
                .map((contract) => {
                  const isAmendment = (contract.avenant_number || 0) > 0;
                  return (
                    <div
                      key={contract._id}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div className="py-3 px-4 flex justify-between items-center gap-3">
                        <div className="flex gap-3 items-center min-w-0">
                          <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <DocumentIcon wh={20} color="#374151" />
                          </div>
                          <div className="text-left min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {isAmendment
                                ? `Avenant n°${contract.avenant_number}`
                                : 'Contrat'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <BadgeSquare type="8" size="xs" />
                          <BadgeSquare type="9" customLabel={`N°${contract.id_bestt}`} size="xs" />
                          <Tooltip text="Supprimer" placement="top">
                            <div
                              className="cursor-pointer"
                              onClick={() => deletedContractHistory(contract._id)}
                            >
                              <CrossIcon wh={20} color="#374151" />
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatChangeValue(field, value) {
  if (field === 'period' && typeof value === 'object' && value !== null) {
    const start = value.start ? dateWordShort(value.start) : '?';
    const end = value.end ? dateWordShort(value.end) : '?';
    return `${start} – ${end}`;
  }
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  return String(value ?? '');
}

// Config couleurs selon le statut
const AMENDMENT_STATUS_ICON = {
  refused: { bg: 'bg-red-100', iconColor: '#b91c1c' },     // red-100, red-700
  pending: { bg: 'bg-yellow-100', iconColor: '#a16207' },  // yellow-100, yellow-700
  replaced: { bg: 'bg-gray-100', iconColor: '#374151' },   // gray (inchangé)
  accepted: { bg: 'bg-green-100', iconColor: '#15803d' },   // green-100, green-700
};

// The square around it is the card's, as on the contracts; this is what goes
// inside it.
function AmendmentStatusIcon({ status }) {
  const cfg = AMENDMENT_STATUS_ICON[status] || AMENDMENT_STATUS_ICON.pending;

  return (
    <>
      {status === 'refused' && <Close wh={18} color={cfg.iconColor} />}
      {status === 'accepted' && <Check wh={18} color={cfg.iconColor} />}
      {status === 'pending' && <TrianglePendingIcon wh={22} color="#D77A1E" />}
      {status === 'replaced' && <Warning wh={18} color={cfg.iconColor} />}
      {status === 'cancelled' && <Warning wh={18} color={cfg.iconColor} />}
      {!status && <TrianglePendingIcon wh={22} color="#D77A1E" />}
    </>
  );
}

function formatAmendmentDate(prefix, isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const dateStr = d.toLocaleDateString('fr-FR');
  const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${prefix} le ${dateStr} à ${timeStr}`;
}

function getAmendmentSubtitle(request) {
  switch (request.status) {
    case 'refused': {
      const when = request.respondedAt || request.updatedAt || request.createdAt;
      return formatAmendmentDate('Refusée', when);
    }
    case 'accepted':
      return "Acceptée par l'intérimaire";
    case 'cancelled': {
      const when = request.respondedAt || request.updatedAt || request.createdAt;
      return formatAmendmentDate('Annulée', when);
    }
    default:
      return formatAmendmentDate('Envoyée', request.createdAt);
  }
}

function AmendmentRequestItem({ request, canAct, onAction }) {
  const statusCfg = AMENDMENT_STATUS_MAP[request.status] || AMENDMENT_STATUS_MAP.pending;
  const iconCfg =
    AMENDMENT_STATUS_ICON[request.status] || AMENDMENT_STATUS_ICON.pending;
  // Only a request nobody has answered can still be answered, and only an
  // admin proper may answer it — accepting moves a contract and bills a client.
  const showActions = canAct && request.status === 'pending';

  const footer = showActions ? (
    <div className="flex items-center justify-between gap-2">
      <Button
        size="SM"
        color="SECONDARY"
        onClick={() => onAction(request._id, 'cancelled')}
      >
        Annuler la demande
      </Button>
      <div className="flex items-center gap-2">
        <Button
          size="SMI"
          color="SECONDARY"
          onClick={() => onAction(request._id, 'refused')}
        >
          <CrossIcon wh={20} color="#111827" />
        </Button>
        <Button
          size="SMI"
          color="SECONDARY"
          onClick={() => onAction(request._id, 'accepted')}
        >
          <Check wh={20} color="#111827" />
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <PanelCard
      icon={<AmendmentStatusIcon status={request.status} />}
      iconClassName={iconCfg.bg}
      title="Demande de prolongation"
      titleClassName="text-sm font-semibold"
      subtitle={getAmendmentSubtitle(request)}
      badge={
        <BadgeSquare
          type={statusCfg.type}
          customLabel={statusCfg.label}
          size="xs"
          bordered
        />
      }
      collapsible
      defaultCollapsed
      outlinedFoldControl={false}
      contentClassName="divide-y divide-gray-100"
      footer={footer}
    >
      {request.changes?.map((c, i) => (
        <div
          key={i}
          className="flex flex-wrap items-baseline gap-x-1 text-sm py-2.5 px-4"
        >
          <span className="font-medium text-gray-700">{CHANGE_FIELD_LABELS[c.field] || c.field}</span>
          <span className="text-gray-700"> : </span>
          <span className="line-through text-gray-400">{formatChangeValue(c.field, c.before)}</span>
          <span className="text-gray-400"> → </span>
          <span className="text-gray-900 font-medium">{formatChangeValue(c.field, c.after)}</span>
        </div>
      ))}
    </PanelCard>
  );
}

function ContractCardItem({
  contract,
  companyContract,
  isLatest,
  isAmendment,
  missionId,
  idx,
  hasUserAssigned,
  loadingContractGeneration,
  loadingSignatureMissionId,
  generationContractSubmit,
  regenerateAmendmentSubmit,
  updateContractSubmit,
  sendSignatureMissionSubmit,
  onPreview,
}) {
  const twSigned = contract.status === 1 || contract.status === '1';
  const coSigned = companyContract?.status === 1 || companyContract?.status === '1';

  let subtitle;
  if (twSigned && coSigned) subtitle = 'Signé par intérimaire & Entreprise';
  else if (twSigned) subtitle = "Signé par l'intérimaire";
  else if (coSigned) subtitle = "Signé par l'entreprise";
  else if (!contract.id_bestt) subtitle = 'Non généré';
  else if (contract.yousign_id || companyContract?.yousign_id) subtitle = 'Envoyé pour signature';
  else subtitle = 'À envoyer pour signature';

  // What these cards differ by: the frame is shared, the actions are not.
  // The design draws them outlined and prefixed by the icon of an action taken
  // again — both re-issue a document that already exists.
  const footer = hasUserAssigned ? (
    <div className="flex justify-start gap-2 flex-wrap">
                {isLatest && (
                  !loadingContractGeneration ? (
                    <Button
                      size="SM"
                      color="SECONDARY"
                      onClick={() =>
                        contract.id_bestt
                          ? updateContractSubmit(missionId)
                          : isAmendment && contract.amendmentRequestId
                            ? regenerateAmendmentSubmit(missionId, contract)
                            : generationContractSubmit(missionId)
                      }
                    >
                      <RefreshIcon wh={18} color="#374151" />
                      {contract.id_bestt
                        ? `Mettre à jour ${isAmendment ? "l'avenant" : 'le contrat'}`
                        : `Générer ${isAmendment ? "l'avenant" : 'le contrat'}`}
                    </Button>
                  ) : (
                    <Button size="SM" color="DISABLED">
                      {contract.id_bestt ? 'Mise à jour' : 'Génération'} ...
                    </Button>
                  )
                )}
                {loadingSignatureMissionId === (contract._id || idx) ? (
                  <Button size="SM" color="DISABLED" disabled>
                    Envoi pour signature ...
                  </Button>
                ) : (
                  <Button
                    size="SM"
                    color={contract.id_bestt ? 'SECONDARY' : 'DISABLED'}
                    onClick={
                      contract.id_bestt
                        ? () => sendSignatureMissionSubmit(missionId, contract._id || idx, contract.avenant_number || 0)
                        : null
                    }
                  >
                    <RefreshIcon wh={18} color={contract.id_bestt ? '#374151' : '#9CA3AF'} />
                    {contract.yousign_id ? 'Renvoyer' : 'Envoyer'} pour signature
                  </Button>
                )}
    </div>
  ) : null;

  return (
    <PanelCard
      icon={<Signer wh={20} color="#374151" />}
      title={
        isAmendment ? `Avenant n°${contract.avenant_number || 0}` : 'Contrat'
      }
      titleClassName="text-sm font-semibold"
      subtitle={subtitle}
      badge={
        // An amendment has a BeSTT number too, and the design shows it: it
        // was the one thing still telling the two cards apart.
        contract.id_bestt ? (
          <BadgeSquare type="9" customLabel={`N°${contract.id_bestt}`} size="xs" bordered />
        ) : null
      }
      collapsible
      defaultCollapsed
      outlinedFoldControl={false}
      contentClassName="divide-y divide-gray-100"
      footer={footer}
    >
      <>
          <div className="flex justify-between gap-1 text-sm items-center py-2.5 px-4">
            <div>Intérimaire</div>
            <div className="flex items-center gap-2">
              <BadgeSquare type={twSigned ? '10' : '9'} size="xs" bordered />
              {/* One eye per signatory rather than one per card: the two
                  parties sign their own copy, so each is its own document.
                  Shown whatever the signature status, as the design does — an
                  unsigned contract is still a contract to read. */}
              <button
                type="button"
                className={cardIconButtonClass}
                aria-label="Voir le contrat de l'intérimaire"
                onClick={() =>
                  onPreview?.({ party: 'tempworker', contract, signed: twSigned })
                }
              >
                <EyeIcon wh={18} color="#374151" />
              </button>
            </div>
          </div>
          <div className="flex justify-between gap-1 text-sm items-center py-2.5 px-4">
            <div>Entreprise</div>
            <div className="flex items-center gap-2">
              <BadgeSquare type={coSigned ? '10' : '9'} size="xs" bordered />
              <button
                type="button"
                className={cardIconButtonClass}
                aria-label="Voir le contrat de l'entreprise"
                onClick={() =>
                  onPreview?.({
                    party: 'company',
                    contract: companyContract ?? contract,
                    signed: coSigned,
                  })
                }
              >
                <EyeIcon wh={18} color="#374151" />
              </button>
            </div>
          </div>
      </>
    </PanelCard>
  );
}

const AdminMissionDetails = ({
  missionId,
  searchMissionResult,
  setSearchMissionResult,
  initialPage,
  isSlideOver = false,
}) => {
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const [, setSearchParams] = useSearchParams();
  const {
    modalDataReturn,
    setModalDataReturn,
    setModalData,
    objID,
    setObjID,
    setTarget,
    assignationMissionTempWorker,
    setAssignationMissionTempWorker,
    modifyMissionDetailData,
    setModifyMissionDetailData,
    listChangesMissionDetail,
    setListChangesMissionDetail,
    setIsChangesNeedsNewContractMissionDetail,
    setAdminType,
    setAdminTempWorkerId,
    searchTempWorkerAssignationResult,
    setSearchTempWorkerAssignationResult,
    searchTempWorkerAssignationRefusedResult,
    setSearchTempWorkerAssignationRefusedResult,
    searchTempWorkerAssignationPendingContactResult,
    setSearchTempWorkerAssignationPendingContactResult,
    updateTempWorkerPendingContactState,
    infoMission,
    setInfoMission,
    searchTempWorkerInviteResult,
    setSearchTempWorkerInviteResult,
    searchTempWorkerResult,
    setSearchTempWorkerResult,
    tempWorkerToRefuse,
    setTempWorkerToRefuse,
    setRefuseMissionId,
    setSlideOverCandidatesList,
    setSlideOverCurrentIndex,
    setSlideOverMissionActions,
  } = UseContext();
  const [missionDateStart, setMissionDateStart] = useState('');
  const [originalMissionDateStart, setOriginalMissionDateStart] = useState('');
  const [missionDateEnd, setMissionDateEnd] = useState('');
  const [originalMissionDateEnd, setOriginalMissionDateEnd] = useState('');
  const [
    isModificationDateMissionAffectBesttContract,
    setIsModificationDateMissionAffectBesttContract,
  ] = useState(false);
  const [searchTempWorker, setSearchTempWorker] = useState('');
  const [searchTempWorkerResultWaiting, setSearchTempWorkerResultWaiting] =
    useState(false);
  const [searchTempWorkerResultLoadData, setSearchTempWorkerResultLoadData] =
    useState(false);
  const [isTempWorkerResultCompleteOpen, setIsTempWorkerResultCompleteOpen] =
    useState(true);
  const [isTempWorkerResultPartialOpen, setIsTempWorkerResultPartialOpen] =
    useState(true);
  const [
    isTempWorkerResultNotUpdatedOpen,
    setIsTempWorkerResultNotUpdatedOpen,
  ] = useState(true);
  const [
    isTempWorkerResultIncompleteOpen,
    setIsTempWorkerResultIncompleteOpen,
  ] = useState(true);
  const [
    isTempWorkerResultSignupPendingOpen,
    setIsTempWorkerResultSignupPendingOpen,
  ] = useState(true);
  const [openRefusedCategoryAccordions, setOpenRefusedCategoryAccordions] = useState({});
  const [openInvitationAccordions, setOpenInvitationAccordions] = useState({});
  const [reasonRefusesList, setReasonRefusesList] = useState([]);
  const [
    isAssignationMissionAffectAssignedMission,
    setIsAssignationMissionAffectAssignedMission,
  ] = useState(false);
  const [selectedTempWorkerAssignation, setSelectedTempWorkerAssignation] =
    useState(false);
  const [selectedTempWorkerRefused, setSelectedTempWorkerRefused] =
    useState(false);
  const [
    errorAssignationMissionIdentityFiles,
    setErrorAssignationMissionIdentityFiles,
  ] = useState(false);
  const [loadingSignatureMissionId, setLoadingSignatureMissionId] = useState(null);
  const [loadingContractGeneration, setLoadingContractGeneration] =
    useState(false);
  const [typeSearchTempWorkerAssignation, setTypeSearchTempWorkerAssignation] =
    useState(1);
  const [countTempWorkerPostulate, setCountTempWorkerPostulate] = useState(0);
  const [countTempWorkerRefused, setCountTempWorkerRefused] = useState(0);
  const [countTempWorkerPendingContact, setCountTempWorkerPendingContact] = useState(0);
  const [countUsersInvite, setCountUsersInvite] = useState(0);
  const [isInfoMissionWaiting, setIsInfoMissionWaiting] = useState(true);
  // The contract preview reads the company's remuneration terms beside the
  // document. They are fetched here, in the background, rather than when the
  // modal opens: the panel used to appear empty and fill in under the reader.
  const [companyRemunerationTerms, setCompanyRemunerationTerms] = useState(null);
  const [loadingAssignationMission, setLoadingAssignationMission] =
    useState(false);
  const [loadingModificationDateMission, setLoadingModificationDateMission] =
    useState(false);
  const [
    isModificationDateMissionDifferentFromOriginal,
    setIsModificationDateMissionDifferentFromOriginal,
  ] = useState(false);

  const [
    searchTempWorkerAssignationToDisplay,
    setSearchTempWorkerAssignationToDisplay,
  ] = useState(false);
  const [
    searchTempWorkerAssignationResultWaiting,
    setSearchTempWorkerAssignationResultWaiting,
  ] = useState(false);
  const [searchTempWorkerAssignation, setSearchTempWorkerAssignation] =
    useState('');
  const [
    searchTempWorkerAssignationResultLoadData,
    setSearchTempWorkerAssignationResultLoadData,
  ] = useState(false);

  const [
    searchTempWorkerAssignationRefusedToDisplay,
    setSearchTempWorkerAssignationRefusedToDisplay,
  ] = useState(false);
  const [
    searchTempWorkerAssignationRefusedResultWaiting,
    setSearchTempWorkerAssignationRefusedResultWaiting,
  ] = useState(false);
  const [
    searchTempWorkerAssignationRefused,
    setSearchTempWorkerAssignationRefused,
  ] = useState('');
  const [
    searchTempWorkerAssignationRefusedResultLoadData,
    setSearchTempWorkerAssignationRefusedResultLoadData,
  ] = useState(false);

  const [
    searchTempWorkerAssignationPendingContactToDisplay,
    setSearchTempWorkerAssignationPendingContactToDisplay,
  ] = useState(false);
  const [
    searchTempWorkerAssignationPendingContactResultWaiting,
    setSearchTempWorkerAssignationPendingContactResultWaiting,
  ] = useState(false);

  const [
    searchTempWorkerInviteResultWaiting,
    setSearchTempWorkerInviteResultWaiting,
  ] = useState(false);
  const [searchTempWorkerInvite, setSearchTempWorkerInvite] = useState('');
  const [activityRefreshNonce, setActivityRefreshNonce] = useState(0);

  const [missionPage, setMissionPage] = useState(1);
  const [currentPageTempWorker, setCurrentPageTempWorker] = useState(1);
  const [limitElementTempWorker, setLimitElementTempWorker] = useState(20);
  const [totalPagesTempWorker, setTotalPagesTempWorker] = useState(0);
  const [countPerPageTempWorker, setCountPerPageTempWorker] = useState(0);
  const [countTotalTempWorker, setCountTotalTempWorker] = useState(0);

  const [
    currentPageTempWorkerAssignation,
    setCurrentPageTempWorkerAssignation,
  ] = useState(1);
  const [
    limitElementTempWorkerAssignation,
    setLimitElementTempWorkerAssignation,
  ] = useState(20);
  const [totalPagesTempWorkerAssignation, setTotalPagesTempWorkerAssignation] =
    useState(0);
  const [countTotalTempWorkerAssignation, setCountTotalTempWorkerAssignation] =
    useState(0);

  const [
    currentPageTempWorkerAssignationRefused,
    setCurrentPageTempWorkerAssignationRefused,
  ] = useState(1);
  const [
    limitElementTempWorkerAssignationRefused,
    setLimitElementTempWorkerAssignationRefused,
  ] = useState(20);
  const [
    totalPagesTempWorkerAssignationRefused,
    setTotalPagesTempWorkerAssignationRefused,
  ] = useState(0);
  const [
    countTotalTempWorkerAssignationRefused,
    setCountTotalTempWorkerAssignationRefused,
  ] = useState(0);

  const [
    currentPageTempWorkerAssignationPendingContact,
    setCurrentPageTempWorkerAssignationPendingContact,
  ] = useState(1);
  const [
    limitElementTempWorkerAssignationPendingContact,
    setLimitElementTempWorkerAssignationPendingContact,
  ] = useState(20);
  const [
    totalPagesTempWorkerAssignationPendingContact,
    setTotalPagesTempWorkerAssignationPendingContact,
  ] = useState(0);
  const [
    countTotalTempWorkerAssignationPendingContact,
    setCountTotalTempWorkerAssignationPendingContact,
  ] = useState(0);
  const [searchTempWorkerAssignationPendingContact, setSearchTempWorkerAssignationPendingContact] =
    useState('');

  const activeTabCandidateIds = useMemo(() => {
    const flatten = (data) => {
      if (!data || typeof data !== 'object') return [];
      return Object.values(data)
        .flat()
        .map(tw => tw.id_user || tw._id);
    };
    switch (typeSearchTempWorkerAssignation) {
      case 1: return flatten(searchTempWorkerAssignationToDisplay);
      case 2: return flatten(searchTempWorkerAssignationRefusedToDisplay);
      case 5: return flatten(searchTempWorkerAssignationPendingContactToDisplay);
      case 3: return flatten(searchTempWorkerResult);
      case 4: return (searchTempWorkerInviteResult || []).map(u => u.id_user || u._id);
      default: return [];
    }
  }, [typeSearchTempWorkerAssignation, searchTempWorkerAssignationToDisplay, searchTempWorkerAssignationRefusedToDisplay, searchTempWorkerAssignationPendingContactToDisplay, searchTempWorkerResult, searchTempWorkerInviteResult]);

  const [modifyMissionDetail, setModifyMissionDetail] = useState(false);
  // JBWEB-1139 : la règle salariale est portée par le champ, qui connaît le SMIC.
  const [isSalaryValid, setIsSalaryValid] = useState(true);
  const [qualifications, setQualifications] = useState([]);

  const [addressMissionDetail, setAddressMissionDetail] = useState({});

  const [licences, setLicences] = useState([]);
  const [tools, setTools] = useState([]);
  const [lang, setLang] = useState([]);

  const [isIdentityFileOpen, setIsIdentityFileOpen] = useState({
    identity: false,
    selfie: false,
    visa: false,
    card_vitality: false,
  });

  const [isChangesModifyMissionDetail, setIsChangesModifyMissionDetail] =
    useState(false);
  const [
    listAllChangesModifyMissionDetail,
    setListAllChangesModifyMissionDetail,
  ] = useState([]);
  const [
    isChangesNeedsNewContractModifyMissionDetail,
    setIsChangesNeedsNewContractModifyMissionDetail,
  ] = useState(false);

  const [dropdownNotificationOpen, setDropdownNotificationOpen] =
    useState(false);
  const dropDownNotificationRef = useRef();
  const [dropdownStatusOpen, setDropdownStatusOpen] =
    useState(false);
  const dropDownStatusRef = useRef();

  const { userData } = usefetchdata();
  const [role, setRole] = useState(500);

  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (token) {
      ApiDatabase.getReasonRefusesMission(
        { token },
        (data) => setReasonRefusesList(data || []),
        (err) => console.log(err)
      );
    }
  }, [token]);

  useEffect(() => {
    editMission({ skipCache: false });
  }, [missionId]);

  const editMission = ({ skipCache }) => {
    setIsInfoMissionWaiting(true);

    setMissionDateStart('');
    setOriginalMissionDateStart('');
    setMissionDateEnd('');
    setOriginalMissionDateEnd('');
    setIsModificationDateMissionAffectBesttContract(false);
    setSearchTempWorker('');
    setSearchTempWorkerResultWaiting(false);
    setSearchTempWorkerResultLoadData(false);
    setSearchTempWorkerResult(false);
    setIsAssignationMissionAffectAssignedMission(false);
    setSelectedTempWorkerAssignation(false);
    setSelectedTempWorkerRefused(false);
    setErrorAssignationMissionIdentityFiles(false);
    setLoadingSignatureMissionId(null);
    setLoadingContractGeneration(false);
    setTypeSearchTempWorkerAssignation(1);
    setCountTempWorkerPostulate(0);
    setCountTempWorkerRefused(0);
    setCountTempWorkerPendingContact(0);
    setCountUsersInvite(0);
    setSearchTempWorkerAssignationPendingContactResult(false);
    setSearchTempWorkerAssignationPendingContactToDisplay(false);
    setSearchTempWorkerAssignationPendingContactResultWaiting(false);
    setInfoMission(false);
    setLoadingAssignationMission(false);
    setLoadingModificationDateMission(false);
    setIsModificationDateMissionDifferentFromOriginal(false);
    setSearchTempWorkerAssignationResult(false);
    setSearchTempWorkerAssignationToDisplay(false);
    setSearchTempWorkerInviteResult([]);
    setSearchTempWorkerAssignationResultWaiting(false);
    setSearchTempWorkerInviteResultWaiting(false);
    setSearchTempWorkerAssignation('');
    setSearchTempWorkerAssignationResultLoadData(false);
    setSearchTempWorkerAssignationRefusedResult(false);
    setSearchTempWorkerAssignationRefusedToDisplay(false);
    setSearchTempWorkerAssignationRefusedResultWaiting(false);
    setSearchTempWorkerAssignationRefused('');
    setSearchTempWorkerAssignationRefusedResultLoadData(false);
    setSearchTempWorkerInvite('');
    setOpenRefusedCategoryAccordions({});

    setMissionPage(1);
    setCurrentPageTempWorker(1);
    setLimitElementTempWorker(20);
    setTotalPagesTempWorker(0);
    setCountPerPageTempWorker(0);
    setCountTotalTempWorker(0);

    setCurrentPageTempWorkerAssignation(1);
    setLimitElementTempWorkerAssignation(20);
    setTotalPagesTempWorkerAssignation(0);
    setCountTotalTempWorkerAssignation(0);

    setCurrentPageTempWorkerAssignationRefused(1);
    setLimitElementTempWorkerAssignationRefused(20);
    setTotalPagesTempWorkerAssignationRefused(0);
    setCurrentPageTempWorkerAssignationPendingContact(1);
    setSearchTempWorkerAssignationPendingContact('');
    setCountTotalTempWorkerAssignationRefused(0);

    setModifyMissionDetail(false);

    setAddressMissionDetail({});
    setModifyMissionDetailData({});

    setListChangesMissionDetail([]);
    setIsChangesNeedsNewContractMissionDetail(false);

    setIsIdentityFileOpen({
      identity: false,
      visa: false,
      card_vitality: false,
    });

    setIsChangesModifyMissionDetail(false);
    setListAllChangesModifyMissionDetail(false);
    setIsChangesNeedsNewContractModifyMissionDetail(false);

    manageTempWorkerSearch({ skipCache });
    manageTempWorkerAssignationSearch({ skipCache });
    manageTempWorkerInviteSearch({ skipCache });

    ApiDatabase.getMissionDetailsById(
      { token: token, idMission: missionId },
      (data) => {
        setInfoMission(data);

        setTypeSearchTempWorkerAssignation(1);

        if (initialPage !== undefined) {
          setMissionPage(initialPage);
        } else if (data.user_assigned) {
          setMissionPage(2);
        } else {
          setMissionPage(1);
        }

        setIsInfoMissionWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setInfoMission(false);
          setIsInfoMissionWaiting(false);
        }
      },
      { skipCache }
    );
  };


  useEffect(() => {
    if (infoMission) {
      if (infoMission.duration?.datestart) {
        setMissionDateStart(dateToEpoch(infoMission.duration.datestart));
        setOriginalMissionDateStart(
          dateToEpoch(infoMission.duration.datestart)
        );
      }
      if (infoMission.duration?.dateend) {
        setMissionDateEnd(dateToEpoch(infoMission.duration.dateend));
        setOriginalMissionDateEnd(dateToEpoch(infoMission.duration.dateend));
      }

      if (infoMission.users_invite?.length > 0) {
        setCountUsersInvite(infoMission.users_invite.length);
      } else {
        setCountUsersInvite(0);
      }

      if (
        infoMission.user_assigned &&
        infoMission.contract_company[0]?.id_bestt
      ) {
        setIsAssignationMissionAffectAssignedMission(true);
      } else {
        setIsAssignationMissionAffectAssignedMission(false);
      }
    }
  }, [infoMission]);

  useEffect(() => {
    setCountTempWorkerPostulate(
      countUsersInCategorizedLists(searchTempWorkerAssignationResult)
    );
    setCountTempWorkerRefused(
      countUsersInCategorizedLists(searchTempWorkerAssignationRefusedResult)
    );
    setCountTempWorkerPendingContact(
      countUsersInCategorizedLists(searchTempWorkerAssignationPendingContactResult)
    );
  }, [
    searchTempWorkerAssignationResult,
    searchTempWorkerAssignationRefusedResult,
    searchTempWorkerAssignationPendingContactResult,
  ]);

  useEffect(() => {
    if (infoMission) {
      if (
        (dateToEpoch(missionDateStart).getTime() !==
          dateToEpoch(originalMissionDateStart).getTime() ||
          dateToEpoch(missionDateEnd).getTime() !==
            dateToEpoch(originalMissionDateEnd).getTime()) &&
        infoMission.contract_company[0]?.id_bestt
      ) {
        setIsModificationDateMissionAffectBesttContract(true);
      } else {
        setIsModificationDateMissionAffectBesttContract(false);
      }

      if (
        dateToEpoch(missionDateStart).getTime() !==
          dateToEpoch(originalMissionDateStart).getTime() ||
        dateToEpoch(missionDateEnd).getTime() !==
          dateToEpoch(originalMissionDateEnd).getTime()
      ) {
        setIsModificationDateMissionDifferentFromOriginal(true);
      } else {
        setIsModificationDateMissionDifferentFromOriginal(false);
      }
    }
  }, [missionDateStart, missionDateEnd]);

  const manageTempWorkerSearch = ({ skipCache }) => {
    setSearchTempWorkerResultWaiting(true);
    setSearchTempWorkerResultLoadData(true);

    ApiDatabase.searchTempWorker(
      {
        token,
        missionId: missionId,
        search: searchTempWorker,
        page: currentPageTempWorker,
        limit: limitElementTempWorker,
      },
      (data) => {
        setSearchTempWorkerResult(data.users);

        setCountTotalTempWorker(data.count);
        setCountPerPageTempWorker(data.countPerPage);
        setTotalPagesTempWorker(data.countPages);

        setSearchTempWorkerResultWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          setSearchTempWorkerResult(false);
          console.error(err);
        }
      },
      { skipCache }
    );
  };

  useEffect(() => {
    manageTempWorkerSearch({ skipCache: false });
  }, [currentPageTempWorker]);

  let timerManageTempWorkerSearch = '';
  useEffect(() => {
    timerManageTempWorkerSearch = setTimeout(async () => {
      setCurrentPageTempWorker(1);
      manageTempWorkerSearch({ skipCache: false });
    }, 400);
    return () => clearTimeout(timerManageTempWorkerSearch);
  }, [searchTempWorker]);

  useEffect(() => {
    if (
      (searchTempWorkerResult.length > 0 && selectedTempWorkerAssignation) ||
      (searchTempWorkerAssignationResult.length > 0 &&
        selectedTempWorkerAssignation)
    ) {
      const user =
        (searchTempWorkerResult
          ? searchTempWorkerResult.find(
              (tempWorker) => tempWorker._id === selectedTempWorkerAssignation
            )
          : null) ||
        (searchTempWorkerAssignationResult
          ? searchTempWorkerAssignationResult.find(
              (tempWorker) => tempWorker._id === selectedTempWorkerAssignation
            )
          : null);
      if (user) {
        if (user.tempworker) {
          if (user.tempworker.identity && user.tempworker.card_vitality) {
            //Not added
            if (
              user.tempworker.identity.validated === 0 ||
              user.tempworker.identity.validated === '0' ||
              user.tempworker.card_vitality.validated === 0 ||
              user.tempworker.card_vitality.validated === '0'
            ) {
              setErrorAssignationMissionIdentityFiles(
                "Les documents d'identité n'ont pas étés ajoutés, un mail va être envoyé à l'intérimaire pour qu'il les ajoutent"
              );
            }
            //Added but not verified
            else if (
              user.tempworker.identity.validated === 1 ||
              user.tempworker.identity.validated === '1' ||
              user.tempworker.card_vitality.validated === 1 ||
              user.tempworker.card_vitality.validated === '1'
            ) {
              setErrorAssignationMissionIdentityFiles(
                "Les documents d'identité doivent être vérifiés par un administrateur"
              );
            }
            //Verified
            else if (
              (user.tempworker.identity.validated === 2 ||
                user.tempworker.identity.validated === '2') &&
              (user.tempworker.card_vitality.validated === 2 ||
                user.tempworker.card_vitality.validated === '2')
            ) {
              //Identity and card vitality data is missing
              if (
                !user.tempworker.identity.number_identity ||
                !user.tempworker.card_vitality.social_security
              ) {
                setErrorAssignationMissionIdentityFiles(
                  "Les documents d'identité sont validés mais les données sont manquantes, contactez un développeur"
                );
              } else {
                setErrorAssignationMissionIdentityFiles(false);
              }
            }
            //Refused
            else if (
              user.tempworker.identity.validated === 3 ||
              user.tempworker.identity.validated === '3' ||
              user.tempworker.card_vitality.validated === 3 ||
              user.tempworker.card_vitality.validated === '3'
            ) {
              setErrorAssignationMissionIdentityFiles(
                "Les documents d'identité ont été refusés, un mail de rappel sera envoyé"
              );
            }
          }
        }
      }
    }
  }, [selectedTempWorkerAssignation]);

  useEffect(() => {
    if (userData?.role) {
      setRole(userData.role);
    }
  }, [userData]);

  const missionDateSubmit = (id_mission) => {
    //toggle('ad_mission_date_confirmation')
    setLoadingModificationDateMission(true);

    const dateStart =
      dateToEpoch(missionDateStart).getFullYear() +
      '/' +
      ('0' + (dateToEpoch(missionDateStart).getMonth() + 1))
        .toString()
        .slice(-2) +
      '/' +
      ('0' + dateToEpoch(missionDateStart).getDate()).toString().slice(-2);
    const dateEnd =
      dateToEpoch(missionDateEnd).getFullYear() +
      '/' +
      ('0' + (dateToEpoch(missionDateEnd).getMonth() + 1))
        .toString()
        .slice(-2) +
      '/' +
      ('0' + dateToEpoch(missionDateEnd).getDate()).toString().slice(-2);

    ApiDatabase.modifyMissionPeriod(
      {
        idMission: id_mission,
        dateStart: dateStart,
        dateEnd: dateEnd,
        token,
      },
      async (data) => {
        setLoadingModificationDateMission(false);
        toast.success('La période de la mission a bien été modifiée');
        for (let i = 0; i < searchMissionResult.length; i++) {
          if (searchMissionResult[i].id_mission_real === id_mission) {
            searchMissionResult[i].duration = {
              datestart: dateToEpoch(missionDateStart).toISOString(),
              dateend: dateToEpoch(missionDateEnd).toISOString(),
            };
            break;
          }
        }
        setSearchMissionResult(searchMissionResult);
        await editMission({ skipCache: true });
      },
      (err) => {
        setLoadingModificationDateMission(false);
        toast.error(
          'Une erreur est survenue lors de la modification de la période de la mission : ' +
            err
        );
      }
    );
  };

  const assignationMissionSubmit = (idTempWorker) => {
    setAssignationMissionTempWorker({
      idTempWorker,
      idMission: infoMission._id,
    });
    toggle('ad_mission_assignation_confirmation');
  };

  const sendSignatureMissionSubmit = (missionId, contractId, avenantNumber) => {
    setLoadingSignatureMissionId(contractId);
    ApiDatabase.requestSignContract(
      { token, idMission: missionId, avenantNumber: avenantNumber ?? null },
      async (data) => {
        setLoadingSignatureMissionId(null);
        toast.success(data.message);
        await editMission({ skipCache: true });
      },
      (err) => {
        setLoadingSignatureMissionId(null);
        toast.error(
          "Une erreur est survenue lors de l'envoi du mail de signature : " +
            err.response.data.error
        );
      }
    );
  };

  const generationContractSubmit = (missionId) => {
    setLoadingContractGeneration(true);
    ApiDatabase.requestGenerationContract(
      { token: token, idMission: missionId },
      async (data) => {
        setLoadingContractGeneration(false);
        toast.success('La génération du contrat a bien été effectuée');
        await editMission({ skipCache: true });
      },
      (err) => {
        setLoadingContractGeneration(false);
        toast.error(
          'Une erreur est survenue lors de la génération du contrat : ' +
            err.response.data.error
        );
      }
    );
  };

  // Replaces « Re-générer » : the document BeSTT holds is corrected where it
  // is, rather than re-issued under a new number.
  const updateContractSubmit = (missionId) => {
    setLoadingContractGeneration(true);
    ApiDatabase.updateContractFromMission(
      { token, idMission: missionId },
      async (data) => {
        setLoadingContractGeneration(false);
        toast.success(data.success || 'Le contrat a été mis à jour');
        await editMission({ skipCache: true });
      },
      (err) => {
        setLoadingContractGeneration(false);
        toast.error(
          'Erreur lors de la mise à jour du contrat : ' +
            (err.response?.data?.message ||
              err.response?.data?.error ||
              'Erreur inconnue')
        );
      }
    );
  };

  const regenerateAmendmentSubmit = (missionId, contract) => {
    setLoadingContractGeneration(true);
    ApiDatabase.regenerateAmendment(
      {
        token,
        idMission: missionId,
        amendmentRequestId: contract.amendmentRequestId,
        avenantNumber: contract.avenant_number,
      },
      async (data) => {
        setLoadingContractGeneration(false);
        toast.success(data.success || "L'avenant a bien été généré");
        await editMission({ skipCache: true });
      },
      (err) => {
        setLoadingContractGeneration(false);
        toast.error(
          "Erreur lors de la génération de l'avenant : " +
            (err.response?.data?.message || err.response?.data?.error || 'Erreur inconnue')
        );
      }
    );
  };

  const manageTempWorkerAssignationSearch = ({ skipCache }) => {
    setSearchTempWorkerAssignationResultWaiting(true);
    setSearchTempWorkerAssignationResultLoadData(true);
    setSearchTempWorkerAssignationRefusedResultWaiting(true);
    setSearchTempWorkerAssignationRefusedResultLoadData(true);
    setSearchTempWorkerAssignationPendingContactResultWaiting(true);

    const searchByTab = {
      1: searchTempWorkerAssignation,
      2: searchTempWorkerAssignationRefused,
      5: searchTempWorkerAssignationPendingContact,
    };

    ApiDatabase.getTempWorkerPostulateMission(
      {
        token: token,
        idMission: missionId,
        search: searchByTab[typeSearchTempWorkerAssignation] || '',
      },
      (data) => {
        let refusedUsers = {
          complete: [],
          partial: [],
          not_updated: [],
          incomplete: [],
          signup_pending: [],
        };

        let otherUsers = {
          complete: [],
          partial: [],
          not_updated: [],
          incomplete: [],
          signup_pending: [],
        };

        let pendingContactUsers = {
          complete: [],
          partial: [],
          not_updated: [],
          incomplete: [],
          signup_pending: [],
        };

        const categories = ['complete', 'partial', 'not_updated', 'incomplete', 'signup_pending'];

        categories.forEach((category) => {
          if (data.users[category]) {
            refusedUsers[category] = data.users[category].filter(
              (user) => user.refused === true
            );
            pendingContactUsers[category] = data.users[category].filter(
              (user) => user.refused === false && user.pendingContact === true
            );
            otherUsers[category] = data.users[category].filter(
              (user) => user.refused === false && user.pendingContact !== true
            );
          } else {
            refusedUsers[category] = [];
            otherUsers[category] = [];
            pendingContactUsers[category] = [];
          }
        });

        setSearchTempWorkerAssignationRefusedResult(refusedUsers);
        setSearchTempWorkerAssignationPendingContactResult(pendingContactUsers);
        setSearchTempWorkerAssignationResult(otherUsers);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          setSearchTempWorkerAssignationRefusedResultWaiting(false);
          setSearchTempWorkerAssignationRefusedResult(false);
          setSearchTempWorkerAssignationResultWaiting(false);
          setSearchTempWorkerAssignationResult(false);
          console.error(err);
        }
      },
      { skipCache }
    );
  };

  useEffect(() => {
    if (searchTempWorkerAssignationResult) {
      setSearchTempWorkerAssignationResultWaiting(true);

      // Annotate each item with its category
      const searchTempWorkerAssignationToDisplay = Object.entries(
        searchTempWorkerAssignationResult
      ).flatMap(([category, items]) =>
        items.map((item) => ({ ...item, category }))
      );

      const sortedResults = searchTempWorkerAssignationToDisplay;

      setCountTotalTempWorkerAssignation(sortedResults.length);
      setTotalPagesTempWorkerAssignation(
        Math.ceil(sortedResults.length / limitElementTempWorkerAssignation)
      );
      const start =
        (currentPageTempWorkerAssignation - 1) *
        limitElementTempWorkerAssignation;
      const end = start + limitElementTempWorkerAssignation;

      const paginatedResults = sortedResults.slice(start, end);

      // Group the paginated results back into their respective categories
      const paginatedCategorizedResults = {
        complete: [],
        partial: [],
        not_updated: [],
        incomplete: [],
        signup_pending: [],
      };

      paginatedResults.forEach((item) => {
        const category = item.category;
        if (paginatedCategorizedResults[category]) {
          paginatedCategorizedResults[category].push(item);
        }
      });

      setSearchTempWorkerAssignationToDisplay(paginatedCategorizedResults);
      setSearchTempWorkerAssignationResultWaiting(false);
    }
  }, [
    searchTempWorkerAssignationResult,
    currentPageTempWorkerAssignation,
  ]);

  useEffect(() => {
    if (searchTempWorkerAssignationRefusedResult) {
      setSearchTempWorkerAssignationRefusedResultWaiting(true);

      // Annotate each item with its category
      const searchTempWorkerAssignationRefusedToDisplay = Object.entries(
        searchTempWorkerAssignationRefusedResult
      ).flatMap(([category, items]) =>
        items.map((item) => ({ ...item, category }))
      );

      const sortedResults = searchTempWorkerAssignationRefusedToDisplay;

      setCountTotalTempWorkerAssignationRefused(sortedResults.length);
      setTotalPagesTempWorkerAssignationRefused(
        Math.ceil(
          sortedResults.length / limitElementTempWorkerAssignationRefused
        )
      );
      const start =
        (currentPageTempWorkerAssignationRefused - 1) *
        limitElementTempWorkerAssignationRefused;
      const end = start + limitElementTempWorkerAssignationRefused;

      const paginatedResults = sortedResults.slice(start, end);

      const reasonIdToCategoryMap = {};
      reasonRefusesList.forEach((r) => {
        reasonIdToCategoryMap[r._id] = r.category || 'autre';
      });

      const groupedByCategory = {};
      paginatedResults.forEach((item) => {
        const cat = item.refusedReasonId
          ? (reasonIdToCategoryMap[item.refusedReasonId] || 'autre')
          : 'sans_motif';
        if (!groupedByCategory[cat]) {
          groupedByCategory[cat] = [];
        }
        groupedByCategory[cat].push(item);
      });

      setSearchTempWorkerAssignationRefusedToDisplay(groupedByCategory);
      setSearchTempWorkerAssignationRefusedResultWaiting(false);
    }
  }, [
    searchTempWorkerAssignationRefusedResult,
    currentPageTempWorkerAssignationRefused,
    reasonRefusesList,
  ]);

  useEffect(() => {
    if (searchTempWorkerAssignationPendingContactResult) {
      setSearchTempWorkerAssignationPendingContactResultWaiting(true);

      const allItems = Object.entries(
        searchTempWorkerAssignationPendingContactResult
      ).flatMap(([category, items]) =>
        items.map((item) => ({ ...item, category }))
      );

      const sortedResults = allItems;

      setCountTotalTempWorkerAssignationPendingContact(sortedResults.length);
      setTotalPagesTempWorkerAssignationPendingContact(
        Math.ceil(
          sortedResults.length / limitElementTempWorkerAssignationPendingContact
        )
      );
      const start =
        (currentPageTempWorkerAssignationPendingContact - 1) *
        limitElementTempWorkerAssignationPendingContact;
      const end = start + limitElementTempWorkerAssignationPendingContact;

      const paginatedResults = sortedResults.slice(start, end);

      const paginatedCategorizedResults = {
        complete: [],
        partial: [],
        not_updated: [],
        incomplete: [],
        signup_pending: [],
      };

      paginatedResults.forEach((item) => {
        const category = item.category;
        if (paginatedCategorizedResults[category]) {
          paginatedCategorizedResults[category].push(item);
        }
      });

      setSearchTempWorkerAssignationPendingContactToDisplay(paginatedCategorizedResults);
      setSearchTempWorkerAssignationPendingContactResultWaiting(false);
    }
  }, [
    searchTempWorkerAssignationPendingContactResult,
    currentPageTempWorkerAssignationPendingContact,
  ]);

  useEffect(() => {
    setCurrentPageTempWorkerAssignationPendingContact(1);
  }, [searchTempWorkerAssignationPendingContact]);

  useEffect(() => {
    const timerManageTempWorkerAssignationSearch = setTimeout(() => {
      setCurrentPageTempWorkerAssignation(1);
      setCurrentPageTempWorkerAssignationRefused(1);
      setCurrentPageTempWorkerAssignationPendingContact(1);
      manageTempWorkerAssignationSearch({ skipCache: false });
    }, 400);

    return () => clearTimeout(timerManageTempWorkerAssignationSearch);
  }, [
    searchTempWorkerAssignation,
    searchTempWorkerAssignationRefused,
    searchTempWorkerAssignationPendingContact,
    typeSearchTempWorkerAssignation,
  ]);

  const manageTempWorkerInviteSearch = ({ skipCache }) => {
    setSearchTempWorkerInviteResultWaiting(true);
    ApiDatabase.getTempWorkerInviteMission(
      {
        token: token,
        idMission: missionId,
        search: searchTempWorkerInvite,
      },
      (data) => {
        setSearchTempWorkerInviteResult(data.users_invite ?? []);
        setSearchTempWorkerInviteResultWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.log(err);
          setSearchTempWorkerInviteResultWaiting(false);
          setSearchTempWorkerInviteResult(false);
        }
      },
      { skipCache }
    );
  };

  useEffect(() => {
    const timerManageTempWorkerInviteSearch = setTimeout(() => {
      manageTempWorkerInviteSearch({ skipCache: false });
    }, 400);

    return () => clearTimeout(timerManageTempWorkerInviteSearch);
  }, [searchTempWorkerInvite]);

  // Acting on a prolongation request also moves the mission's dates and can add
  // an avenant, so the whole detail is reloaded rather than the card patched.
  // The contract and the terms it came from, side by side (JBWEB-1208).
  useEffect(() => {
    const idCompany = infoMission?.company?._id;
    if (!idCompany) return;

    setCompanyRemunerationTerms(null);
    ApiDatabase.getCompanyInfo(
      { token, idCompany },
      (data) => setCompanyRemunerationTerms(data.company),
      (err) => {
        // A failed prefetch costs a spinner, never the preview: the modal
        // fetches them itself when it opens without them.
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [infoMission?.company?._id]);

  const openContractPreview = ({ party, contract, signed }) => {
    setModalData({
      mission: infoMission,
      party,
      signed,
      // The file name is derived server-side from these three, so the browser
      // never has to know the naming convention.
      avenantNumber: contract?.avenant_number ?? contract?.avenant ?? 0,
      companyInfo: companyRemunerationTerms,
    });
    toggle('contract_preview');
  };

  const openAmendmentAction = (idRequest, action) => {
    setModalData({ idRequest, action });
    toggle('amendment_request_action');
  };

  const openContractGeneration = () => {
    setModalData({
      idMission: infoMission._id,
      generationLabel: contractGenerationLabel(
        contractGenerationDate(infoMission)
      ),
      isDue: isContractGenerationDue(infoMission),
    });
    toggle('contract_generation');
  };

  useEffect(() => {
    if (modalDataReturn?.type === 'amendment_request_actioned') {
      editMission({ skipCache: true });
      setModalDataReturn({});
    }
    if (modalDataReturn?.type === 'contract_generated') {
      editMission({ skipCache: true });
      setModalDataReturn({});
    }
    if (modalDataReturn === 'mission canceled') {
      setSearchMissionResult(
        searchMissionResult.map((mission) =>
          mission.id_mission_real === infoMission._id
            ? { ...mission, status: 'canceled' }
            : mission
        )
      );
      editMission({ skipCache: true });
      setModalDataReturn({});
    }
    if (modalDataReturn === 'mission_status_updated') {
      editMission({ skipCache: true });
      setModalDataReturn({});
    }
    if (modalDataReturn === 'mission assigned') {
      setMissionPage(2);
      setModalDataReturn({});
    }
    if (modalDataReturn === 'mission unassigned') {
      const missionIdStr = missionId?.toString?.() || missionId;
      setSearchMissionResult((prevData) => {
        if (!Array.isArray(prevData)) return prevData;
        return prevData.map((mission) => {
          const realId =
            mission.id_mission_real?.toString?.() || mission.id_mission_real;
          if (realId === missionIdStr) {
            return {
              ...mission,
              user: null,
              isAssigned: false,
              contract_company: [],
              contract_tempworker: [],
            };
          }
          return mission;
        });
      });
      setModalDataReturn({});
      setMissionPage(1);
      ApiDatabase.getMissionDetailsById(
        { token: token, idMission: missionId },
        (data) => {
          setInfoMission(data);
        },
        (err) => {
          console.log(err);
        },
        { skipCache: true }
      );
    }
    if (modalDataReturn === 'mission invitations sent') {
      manageTempWorkerInviteSearch({ skipCache: true });
      setActivityRefreshNonce((prevData) => prevData + 1);
      setModalDataReturn({});
    }
    if (modalDataReturn === 'mission updated') {
      const missionIdStr = missionId?.toString?.() || missionId;
      setSearchMissionResult((prevData) => {
        if (!Array.isArray(prevData)) return prevData;
        return prevData.map((mission) => {
          const realId =
            mission.id_mission_real?.toString?.() || mission.id_mission_real;
          if (realId !== missionIdStr) return mission;

          const periodChange = listChangesMissionDetail?.find(
            (change) => change.field === 'period'
          );
          const qualificationChange = listChangesMissionDetail?.find(
            (change) => change.field === 'qualification'
          );
          const referenceChange = listChangesMissionDetail?.find(
            (change) => change.field === 'reference'
          );

          return {
            ...mission,
            ...(qualificationChange
              ? { title: qualificationChange.after }
              : modifyMissionDetailData.qualification
                ? { title: modifyMissionDetailData.qualification }
                : {}),
            ...(periodChange || modifyMissionDetailData.period
              ? {
                  duration: {
                    datestart:
                      periodChange?.after?.start ||
                      modifyMissionDetailData.period?.start ||
                      mission.duration?.datestart,
                    dateend:
                      periodChange?.after?.end ||
                      modifyMissionDetailData.period?.end ||
                      mission.duration?.dateend,
                  },
                }
              : {}),
            ...(referenceChange
              ? { reference: referenceChange.after }
              : modifyMissionDetailData.reference
                ? { reference: modifyMissionDetailData.reference }
                : {}),
            ...(objID
              ? {
                  contract_tempworker: [
                    {
                      ...mission.contract_tempworker?.[0],
                      id_bestt: objID,
                    },
                  ],
                  contract_company: [
                    {
                      ...mission.contract_company?.[0],
                      id_bestt: objID,
                    },
                  ],
                }
              : {}),
          };
        });
      });
      setModalDataReturn({});
      setListChangesMissionDetail([]);
      setIsChangesNeedsNewContractMissionDetail(false);
      setModifyMissionDetail(false);
      setObjID('');
      editMission({ skipCache: true });
    }
  }, [modalDataReturn]);

  const openReplacementMission = (idMission) => {
    setSearchParams((prev) => {
      prev.set('mission', idMission);
      return prev;
    });
  };

  const openTempWorkerInfo = (id, target = '') => {
    setTarget(target);
    if (!infoMission.user_assigned) {
      setSlideOverMissionActions({
        missionTitle: infoMission.title,
        missionNumber: infoMission.id_mission,
        onAssign: (twId) => assignationMissionSubmit(twId),
        onRefuse: (twId) => refuseTempWorker(twId),
        onPendingContact: (twId) => pendingContactTempWorker(twId),
      });
    } else {
      setSlideOverMissionActions({
        missionTitle: infoMission.title,
        missionNumber: infoMission.id_mission,
      });
    }
    setObjID(id);
    toggleSlideOver('get_temp_worker');
  };

  const EmptyStateMission = ({ type }) => {
    const configs = {
      candidature: {
        icon: GroupPeople,
        title: 'Aucune candidature',
        description: "Aucune candidature n'a encore été reçue pour cette mission.",
      },
      refus: {
        icon: CrossIcon,
        title: 'Aucun refus',
        description: "Aucun intérimaire n'a été refusé pour cette mission.",
      },
      recherche: {
        icon: SearchLoop,
        title: 'Aucun résultat',
        description: "Aucun intérimaire ne correspond à votre recherche.",
      },
      invitation: {
        icon: BellIcon,
        title: 'Aucune invitation',
        description: "Aucun intérimaire n'a encore été invité pour cette mission.",
      },
      pendingContact: {
        icon: GroupPeople,
        title: 'Aucun candidat en attente',
        description: "Aucun intérimaire n'est en attente de réponse pour cette mission.",
      },
      profil: {
        icon: ProfilIcon,
        title: 'Aucun profil attribué',
        description: "Vous devez attribuer un profil à cette mission.",
      },
    };
    const { icon: Icon, title, description } = configs[type] || configs.recherche;
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Icon wh={32} color="#9CA3AF" />
        </div>
        <div className="text-gray-900 font-semibold text-lg mb-2">{title}</div>
        <div className="text-gray-500 text-sm text-center max-w-md">{description}</div>
      </div>
    );
  };

  // Fonction pour gérer le refus ou l'annulation de refus d'un candidat
  const refuseTempWorker = (idTempWorker) => {
    // Vérifier si le candidat est déjà dans la liste des refusés
    let foundInRefused = false;
    for (const key in searchTempWorkerAssignationRefusedResult) {
      if (searchTempWorkerAssignationRefusedResult.hasOwnProperty(key)) {
        const index = searchTempWorkerAssignationRefusedResult[key].findIndex(
          (data) => data.id_user === idTempWorker
        );
        if (index !== -1) {
          foundInRefused = true;
          break;
        }
      }
    }

    if (foundInRefused) {
      // Candidat déjà refusé → annuler le refus directement (sans modal)
      applyRefuseTempWorkerState(idTempWorker, false);
      ApiDatabase.postTempWorkerAssignationRefused(
        { token, idMission: infoMission._id, idTempWorker },
        (data) => {},
        (err) => {}
      );
    } else {
      // Candidat non refusé → ouvrir le modal pour sélectionner un motif
      setTempWorkerToRefuse(idTempWorker);
      setRefuseMissionId(infoMission._id);
      toggle('mission_refuse_candidat');
    }
  };

  // Fonction pour appliquer les changements d'état local après refus/annulation
  const applyRefuseTempWorkerState = (idTempWorker, isRefusing) => {
    let tempWorkerData = null;
    let keyFound = null;

    if (!isRefusing) {
      // Annulation d'un refus: déplacer de refused vers assignation
      for (const key in searchTempWorkerAssignationRefusedResult) {
        if (searchTempWorkerAssignationRefusedResult.hasOwnProperty(key)) {
          const index = searchTempWorkerAssignationRefusedResult[key].findIndex(
            (data) => data.id_user === idTempWorker
          );
          if (index !== -1) {
            tempWorkerData = searchTempWorkerAssignationRefusedResult[key][index];
            keyFound = key;

            const newRefusedArray = [
              ...searchTempWorkerAssignationRefusedResult[key],
            ];
            newRefusedArray.splice(index, 1);
            setSearchTempWorkerAssignationRefusedResult((prevData) => ({
              ...prevData,
              [key]: newRefusedArray,
            }));
            break;
          }
        }
      }

      if (tempWorkerData && keyFound) {
        const assignationArray =
          searchTempWorkerAssignationResult[keyFound] || [];

        const newAssignationArray = [
          ...assignationArray,
          { ...tempWorkerData, refused: false },
        ];

        newAssignationArray.sort((a, b) => {
          const lastnameA = a.lastname?.toLowerCase() || '';
          const lastnameB = b.lastname?.toLowerCase() || '';
          return lastnameA.localeCompare(lastnameB);
        });

        setSearchTempWorkerAssignationResult((prevData) => ({
          ...prevData,
          [keyFound]: newAssignationArray,
        }));
      }
    } else {
      // Nouveau refus: déplacer de assignation ou pendingContact vers refused
      for (const key in searchTempWorkerAssignationResult) {
        if (searchTempWorkerAssignationResult.hasOwnProperty(key)) {
          const index = searchTempWorkerAssignationResult[key].findIndex(
            (data) => data.id_user === idTempWorker
          );
          if (index !== -1) {
            tempWorkerData = searchTempWorkerAssignationResult[key][index];
            keyFound = key;

            const newAssignationArray = [
              ...searchTempWorkerAssignationResult[key],
            ];
            newAssignationArray.splice(index, 1);
            setSearchTempWorkerAssignationResult((prevData) => ({
              ...prevData,
              [key]: newAssignationArray,
            }));
            break;
          }
        }
      }

      if (!tempWorkerData) {
        for (const key in searchTempWorkerAssignationPendingContactResult) {
          if (searchTempWorkerAssignationPendingContactResult.hasOwnProperty(key)) {
            const index = searchTempWorkerAssignationPendingContactResult[key].findIndex(
              (data) => data.id_user === idTempWorker
            );
            if (index !== -1) {
              tempWorkerData = searchTempWorkerAssignationPendingContactResult[key][index];
              keyFound = key;

              const newArray = [
                ...searchTempWorkerAssignationPendingContactResult[key],
              ];
              newArray.splice(index, 1);
              setSearchTempWorkerAssignationPendingContactResult((prevData) => ({
                ...prevData,
                [key]: newArray,
              }));
              break;
            }
          }
        }
      }

      if (tempWorkerData && keyFound) {
        const refusedArray =
          searchTempWorkerAssignationRefusedResult[keyFound] || [];

        const newRefusedArray = [
          ...refusedArray,
          { ...tempWorkerData, refused: true },
        ];

        newRefusedArray.sort((a, b) => {
          const lastnameA = a.lastname?.toLowerCase() || '';
          const lastnameB = b.lastname?.toLowerCase() || '';
          return lastnameA.localeCompare(lastnameB);
        });

        setSearchTempWorkerAssignationRefusedResult((prevData) => ({
          ...prevData,
          [keyFound]: newRefusedArray,
        }));
      }
    }

    setInfoMission((prevData) => ({
      ...prevData,
      users: prevData?.users.map((user) =>
        user.user_id === idTempWorker
          ? { ...user, refused: isRefusing }
          : user
      ),
    }));

    setSearchMissionResult((prevData) => {
      return prevData.map((mission) => {
        if (mission.id_mission_real === missionId) {
          const userExists = mission.users.some(
            (user) => user.user_id === idTempWorker
          );

          let updatedUsers;
          if (userExists) {
            updatedUsers = mission.users.map((user) =>
              user.user_id === idTempWorker
                ? { ...user, refused: isRefusing }
                : user
            );
          } else {
            const newUser = { user_id: idTempWorker, refused: isRefusing };
            updatedUsers = [...mission.users, newUser];
          }

          return {
            ...mission,
            users: updatedUsers,
          };
        } else {
          return mission;
        }
      });
    });
  };

  // Effet pour gérer le retour du modal de refus
  useEffect(() => {
    if (modalDataReturn?.action === 'refuse_candidat' && modalDataReturn?.idTempWorker) {
      applyRefuseTempWorkerState(modalDataReturn.idTempWorker, true);
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  const pendingContactTempWorker = (idTempWorker) => {
    let foundInPendingContact = false;
    if (searchTempWorkerAssignationPendingContactResult) {
      for (const key of ['complete', 'partial', 'not_updated', 'incomplete', 'signup_pending']) {
        if (searchTempWorkerAssignationPendingContactResult[key]) {
          const index = searchTempWorkerAssignationPendingContactResult[key].findIndex(
            (data) => data.id_user === idTempWorker
          );
          if (index !== -1) {
            foundInPendingContact = true;
            break;
          }
        }
      }
    }

    const isPending = !foundInPendingContact;
    updateTempWorkerPendingContactState(idTempWorker, isPending);

    ApiDatabase.postTempWorkerPendingContact(
      { token, idMission: infoMission._id, idTempWorker },
      (data) => {},
      (err) => {}
    );
  };

  const deletedContractHistory = (contractHistoryId) => {
    setInfoMission((prevData) => {
      const updatedContractHistories = prevData.contractHistories.map(
        (contractHistory) => {
          if (contractHistory._id === contractHistoryId) {
            return { ...contractHistory, status: 'deleted' };
          }
          return contractHistory;
        }
      );
      return { ...prevData, contractHistories: updatedContractHistories };
    });

    ApiDatabase.postDeleteContractHistory(
      { token, idContract: contractHistoryId },
      (data) => {},
      (err) => {}
    );
  };

  useEffect(() => {
    if (infoMission) {
      ApiDatabase.getQualificationsActive(
        { token },
        (data) => {
          const updatedData = data.map((item) => ({
            ...item,
            checked: item.name === infoMission.title,
          }));

          setQualifications(updatedData);
        },
        (err) => {}
      );
    }
  }, [infoMission]);

  const updateQualification = (updatedQualification) => {
    const updatedQualifications = [...qualifications];

    for (let i = 0; i < updatedQualifications.length; i++) {
      for (let j = 0; j < updatedQualification.length; j++) {
        if (updatedQualifications[i]._id === updatedQualification[j].value) {
          updatedQualifications[i].checked = updatedQualification[j].checked;
        }
      }
    }

    setQualifications(updatedQualifications);
  };

  useEffect(() => {
    if (infoMission) {
      setAddressMissionDetail(infoMission.address);
      setModifyMissionDetailData({
        qualification: infoMission.title,
        period: {
          start: dateToEpoch(infoMission.duration.datestart),
          end: dateToEpoch(infoMission.duration.dateend),
        },
        schedule: infoMission.schedules,
        salary: infoMission.salary.wage_per_hour,
        address: infoMission.address,
        reason: {
          reason: infoMission.reason_detail.reason,
          type: infoMission.reason_detail.type,
          proof: infoMission.reason_detail.proof || null,
          proof_firstname: infoMission.reason_detail.proof_firstname || null,
          proof_lastname: infoMission.reason_detail.proof_lastname || null,
          proof_job: infoMission.reason_detail.proof_job || null,
          proof_description:
            infoMission.reason_detail.proof_description || null,
          proof_date: infoMission.reason_detail.proof_date || null,
        },
        licences: infoMission.licences,
        languages: infoMission.language,
        tools: infoMission.tools,
        reference: infoMission.reference,
        other: infoMission.other,
        other_interim: infoMission.other_interim,
      });

      if (modifyMissionDetail) {
        ApiDatabase.getAllLicence(
          (data) => {
            const updatedData = data.map((item) => ({
              name: item.name,
              checked: infoMission.licences.includes(item.name),
            }));

            setLicences(updatedData);
          },
          (err) => {
            console.warn(err);
          }
        );
        ApiDatabase.getListTools(
          (data) => {
            const updatedData = data.map((item) => ({
              name: item.name,
              checked: infoMission.tools.includes(item.name),
            }));

            setTools(updatedData);
          },
          (err) => {
            console.warn(err);
          }
        );
        ApiDatabase.getListLanguages(
          (data) => {
            const updatedData = data.map((item) => ({
              name: item.name,
              checked: infoMission.language.includes(item.name),
            }));

            setLang(updatedData);
          },
          (err) => {
            console.warn(err);
          }
        );
      }
    }
  }, [infoMission, modifyMissionDetail]);

  useEffect(() => {
    if (addressMissionDetail) {
      setModifyMissionDetailData((prevData) => ({
        ...prevData,
        address: addressMissionDetail,
      }));
    }
  }, [addressMissionDetail]);

  useEffect(() => {
    const checkedQualification = qualifications.find(
      (qualification) => qualification.checked === true
    );
    if (checkedQualification) {
      setModifyMissionDetailData((prevData) => {
        if (!prevData?.period?.start || !prevData?.period?.end) {
          return prevData;
        }
        return {
          ...prevData,
          qualification: checkedQualification.name,
        };
      });
    }
  }, [qualifications]);

  const updateLicence = (updatedLicence) => {
    const updatedLicences = [...licences];

    for (let i = 0; i < updatedLicences.length; i++) {
      for (let j = 0; j < updatedLicence.length; j++) {
        if (updatedLicences[i].name === updatedLicence[j].value) {
          updatedLicences[i].checked = updatedLicence[j].checked;
        }
      }
    }

    setLicences(updatedLicences);
  };

  useEffect(() => {
    const licencesChecked = licences
      .filter((licence) => licence.checked)
      .map((licence) => licence.name);

    setModifyMissionDetailData((prevData) => ({
      ...prevData,
      licences: licencesChecked,
    }));
  }, [licences]);

  const updateTool = (updatedTool) => {
    const updatedTools = [...tools];

    for (let i = 0; i < updatedTools.length; i++) {
      for (let j = 0; j < updatedTool.length; j++) {
        if (updatedTools[i].name === updatedTool[j].value) {
          updatedTools[i].checked = updatedTool[j].checked;
        }
      }
    }

    setTools(updatedTools);
  };

  useEffect(() => {
    const toolsChecked = tools
      .filter((tool) => tool.checked)
      .map((tool) => tool.name);

    setModifyMissionDetailData((prevData) => ({
      ...prevData,
      tools: toolsChecked,
    }));
  }, [tools]);

  const updateLanguage = (updatedLanguage) => {
    const updatedLanguages = [...lang];

    for (let i = 0; i < updatedLanguages.length; i++) {
      for (let j = 0; j < updatedLanguage.length; j++) {
        if (updatedLanguages[i].name === updatedLanguage[j].value) {
          updatedLanguages[i].checked = updatedLanguage[j].checked;
        }
      }
    }

    setLang(updatedLanguages);
  };

  useEffect(() => {
    const langChecked = lang
      .filter((language) => language.checked)
      .map((language) => language.name);

    setModifyMissionDetailData((prevData) => ({
      ...prevData,
      languages: langChecked,
    }));
  }, [lang]);

  const editMissionDetailReason = () => {
    toggle('mission_detail_reason');
  };

  const detectChanges = () => {
    const changes = [];
    let needsNewContract = false;
    let hasChanges = false;

    if (
      infoMission &&
      modifyMissionDetailData.qualification &&
      modifyMissionDetailData.period?.start &&
      modifyMissionDetailData.period?.end &&
      Array.isArray(modifyMissionDetailData.schedule) &&
      infoMission.duration?.datestart &&
      infoMission.duration?.dateend
    ) {
      const fieldsRequiringNewContract = [];

      const addChange = (field, before, after) => {
        if (
          fieldsRequiringNewContract.includes(field) &&
          infoMission.user_assigned
        ) {
          needsNewContract = true;
        }
        hasChanges = true;
        changes.push({ field, before, after });
      };

      const arraysAreEqual = (a, b) => {
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        const aSorted = [...a].sort();
        const bSorted = [...b].sort();
        for (let i = 0; i < aSorted.length; i++) {
          if (aSorted[i] !== bSorted[i]) return false;
        }
        return true;
      };

      if (modifyMissionDetailData.qualification !== infoMission.title) {
        addChange(
          'qualification',
          infoMission.title,
          modifyMissionDetailData.qualification
        );
      }

      const originalStart = dateToEpoch(infoMission.duration.datestart);
      const originalEnd = dateToEpoch(infoMission.duration.dateend);
      const originalStartGetTime = originalStart.getTime();
      const originalEndGetTime = originalEnd.getTime();
      const modifyMissionDetailStart = modifyMissionDetailData.period.start;
      const modifyMissionDetailEnd = modifyMissionDetailData.period.end;
      const modifyMissionDetailStartGetTime =
        modifyMissionDetailStart.getTime();
      const modifyMissionDetailEndGetTime = modifyMissionDetailEnd.getTime();

      if (
        modifyMissionDetailStartGetTime !== originalStartGetTime ||
        modifyMissionDetailEndGetTime !== originalEndGetTime
      ) {
        addChange(
          'period',
          {
            start: dateToIsoString(originalStart),
            end: dateToIsoString(originalEnd),
          },
          {
            start: dateToIsoString(modifyMissionDetailStart),
            end: dateToIsoString(modifyMissionDetailEnd),
          }
        );
      }

      if (
        modifyMissionDetailData.schedule.length !== infoMission.schedules.length
      ) {
        addChange(
          'schedule',
          infoMission.schedules,
          modifyMissionDetailData.schedule
        );
      } else {
        outerLoop: for (
          let i = 0;
          i < modifyMissionDetailData.schedule.length;
          i++
        ) {
          const modifyScheduleDays = modifyMissionDetailData.schedule[i].days;
          const infoScheduleDays = infoMission.schedules[i].days;

          const filterDaysWithPeriods = (days) =>
            days.filter(
              (day) => day.periods && Object.keys(day.periods).length > 0
            );

          const modifyScheduleDaysWithPeriods =
            filterDaysWithPeriods(modifyScheduleDays);
          const infoScheduleDaysWithPeriods =
            filterDaysWithPeriods(infoScheduleDays);

          if (
            modifyScheduleDaysWithPeriods.length !==
            infoScheduleDaysWithPeriods.length
          ) {
            addChange(
              'schedule',
              infoMission.schedules,
              modifyMissionDetailData.schedule
            );
            break;
          }

          const sortDays = (days) =>
            [...days].sort((a, b) => dateToEpoch(a.date) - dateToEpoch(b.date));

          const modifyDaysSorted = sortDays(modifyScheduleDaysWithPeriods);
          const infoDaysSorted = sortDays(infoScheduleDaysWithPeriods);

          for (let j = 0; j < modifyDaysSorted.length; j++) {
            const modifyDay = modifyDaysSorted[j];
            const infoDay = infoDaysSorted[j];

            if (
              dateToEpoch(modifyDay.date).getTime() !==
              dateToEpoch(infoDay.date).getTime()
            ) {
              addChange(
                'schedule',
                infoMission.schedules,
                modifyMissionDetailData.schedule
              );
              break outerLoop;
            }

            const modifyPeriods = modifyDay.periods;
            const infoPeriods = infoDay.periods;

            if (!modifyPeriods || !infoPeriods) {
              addChange(
                'schedule',
                infoMission.schedules,
                modifyMissionDetailData.schedule
              );
              break outerLoop;
            }

            const periodKeys = [
              'periode_one_start',
              'periode_one_end',
              'periode_two_start',
              'periode_two_end',
              'periode_three_start',
              'periode_three_end',
            ];

            for (const key of periodKeys) {
              if (modifyPeriods[key] !== infoPeriods[key]) {
                addChange(
                  'schedule',
                  infoMission.schedules,
                  modifyMissionDetailData.schedule
                );
                break outerLoop;
              }
            }
          }
        }
      }

      if (
        parseFloat(modifyMissionDetailData.salary) !==
        parseFloat(infoMission.salary.wage_per_hour)
      ) {
        addChange(
          'salary',
          infoMission.salary.wage_per_hour,
          modifyMissionDetailData.salary
        );
      }

      if (modifyMissionDetailData.address !== infoMission.address) {
        addChange(
          'address',
          infoMission.address,
          modifyMissionDetailData.address
        );
      }

      const reasonFields = [
        'reason',
        'type',
        'proof',
        'proof_firstname',
        'proof_lastname',
        'proof_job',
        'proof_description',
        'proof_date',
      ];

      const reasonDetailChanged = reasonFields.some(
        (field) =>
          modifyMissionDetailData.reason[field] !==
          (infoMission.reason_detail[field] || null)
      );

      if (reasonDetailChanged) {
        addChange(
          'reason',
          infoMission.reason_detail,
          modifyMissionDetailData.reason
        );
      }

      if (
        !arraysAreEqual(modifyMissionDetailData.licences, infoMission.licences)
      ) {
        addChange(
          'licences',
          infoMission.licences,
          modifyMissionDetailData.licences
        );
      }

      if (
        !arraysAreEqual(modifyMissionDetailData.languages, infoMission.language)
      ) {
        addChange(
          'languages',
          infoMission.language,
          modifyMissionDetailData.languages
        );
      }

      if (!arraysAreEqual(modifyMissionDetailData.tools, infoMission.tools)) {
        addChange('tools', infoMission.tools, modifyMissionDetailData.tools);
      }

      if (
        (modifyMissionDetailData.reference || '') !==
        (infoMission.reference || '')
      ) {
        addChange(
          'reference',
          infoMission.reference,
          modifyMissionDetailData.reference
        );
      }

      if ((modifyMissionDetailData.other || '') !== (infoMission.other || '')) {
        addChange('other', infoMission.other, modifyMissionDetailData.other);
      }

      if (
        (modifyMissionDetailData.other_interim || '') !==
        (infoMission.other_interim || '')
      ) {
        addChange(
          'other_interim',
          infoMission.other_interim,
          modifyMissionDetailData.other_interim
        );
      }
    }

    return { hasChanges, changes, needsNewContract };
  };

  useEffect(() => {
    const { hasChanges, changes, needsNewContract } = detectChanges();
    setIsChangesModifyMissionDetail(hasChanges);
    setListAllChangesModifyMissionDetail(changes);
    setIsChangesNeedsNewContractModifyMissionDetail(needsNewContract);
  }, [modifyMissionDetailData]);

  const submitModifyMissionDetail = () => {
    const { hasChanges, changes, needsNewContract } = detectChanges();

    // The rate is refused by the api too; saying it here spares the admin a
    // confirmation modal for a change that was never going to be applied.
    if (!isSalaryValid) {
      toast.error('Corrigez la rémunération avant de confirmer.');
      return;
    }

    if (hasChanges && (role === 500 || role === 515)) {
      setObjID(infoMission._id);
      setListChangesMissionDetail(changes);
      setIsChangesNeedsNewContractMissionDetail(needsNewContract);
      toggle('mission_detail_submit');
    }
  };

  useEffect(() => {
    let handler = (e) => {
      if (
        dropDownNotificationRef.current &&
        !dropDownNotificationRef.current.contains(e.target)
      ) {
        setDropdownNotificationOpen(false);
      }
      if (
        dropDownStatusRef.current &&
        !dropDownStatusRef.current.contains(e.target)
      ) {
        setDropdownStatusOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);

    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [dropDownNotificationRef, dropDownStatusRef]);

  // Pour l'Identity
  const identityData = infoMission.user_assigned_infos?.tempworker?.identity;
  const identityTitle =
    identityData?.type === 'Passeport' ? 'Passeport' : "Carte d'identité";

  const identityValidatedType =
    identityData?.validated === '2' ? '5' : identityData?.validated;

  const identityDetails = [
    {
      label: "Numéro d'identité",
      value: identityData?.number_identity,
    },
    {
      label: 'Ville de naissance',
      value: identityData?.birthplace,
    },
    {
      label: 'Validité',
      value: `${
        identityData?.start_validity
          ? dateWordShort(identityData.start_validity)
          : 'NaN'
      } - ${
        identityData?.end_validity
          ? dateWordShort(identityData.end_validity)
          : 'NaN'
      }`,
    },
  ];

  // Pour le selfie
  const selfieData = infoMission.user_assigned_infos?.tempworker?.selfie;
  const selfieTitle = 'Selfie';

  const selfieValidatedType =
    selfieData?.validated === '2' ? '5' : selfieData?.validated;

  const selfieDetails = [];

  // Pour le Visa
  const visaData = infoMission.user_assigned_infos?.tempworker?.visa;
  const visaTitle = visaData?.type === 'Visa' ? 'Visa' : 'Titre de séjour';

  const visaValidatedType =
    visaData?.validated === '2' ? '5' : visaData?.validated;

  const visaDetails = [
    {
      label: 'Numéro',
      value: visaData?.number_visa,
    },
    {
      label: 'Validité',
      value: `${
        visaData?.start_validity
          ? dateWordShort(visaData.start_validity)
          : 'NaN'
      } - ${
        visaData?.end_validity ? dateWordShort(visaData.end_validity) : 'NaN'
      }`,
    },
  ];

  // Pour la Carte Vitale
  const cardVitalityData =
    infoMission.user_assigned_infos?.tempworker?.card_vitality;
  const cardVitalityTitle = 'Carte vitale';

  const cardVitalityValidatedType =
    cardVitalityData?.validated === '2' ? '5' : cardVitalityData?.validated;

  const cardVitalityDetails = [
    {
      label: 'Numéro',
      value: cardVitalityData?.social_security,
    },
  ];

  // Pour le Justificatif d'adresse
  const proofOfAddressData =
    infoMission.user_assigned_infos?.tempworker?.proof_of_address;
  const proofOfAddressTitle = "Justificatif d'adresse";

  const proofOfAddressValidatedType =
    proofOfAddressData?.validated === '2' ? '5' : proofOfAddressData?.validated;

  // Pour l'Attestation d'hébergement
  const accommodationData =
    infoMission.user_assigned_infos?.tempworker?.accommodation_of_address;
  const accommodationTitle = "Attestation d'hébergement";

  const accommodationValidatedType =
    accommodationData?.validated === '2' ? '5' : accommodationData?.validated;

  // Pour la Pièce d'identité de l'hôte
  const hostIdentityData =
    infoMission.user_assigned_infos?.tempworker?.hostIdentity;
  const hostIdentityTitle = "Pièce d'identité de l'hôte";

  const hostIdentityValidatedType =
    hostIdentityData?.validated === '2' ? '5' : hostIdentityData?.validated;

  const openDocument = (type, type2) => {
    setAdminType(type);

    if (type2 === 'view') {
      setAdminTempWorkerId('');
      toggle('ad_file_view');
    } else {
      setAdminTempWorkerId(infoMission.user_assigned_infos?.tempworker?._id);
      if (type === 'identity') toggle('ad_file_accept_identity');
      else if (type === 'visa') toggle('ad_file_accept_visa');
      else if (type === 'card_vitality') toggle('ad_file_accept_card_vitality');
      else if (type === 'selfie') toggle('ad_file_accept_simple');
      else if (type === 'proof_of_address') toggle('ad_file_accept_simple');
      else if (type === 'accommodation_of_address')
        toggle('ad_file_accept_simple');
      else if (type === 'hostIdentity') toggle('ad_file_accept_simple');
      else toggle('ad_file_view');
    }
  };

  const panelClassName = isSlideOver
    ? 'border rounded'
    : 'h-full border rounded overflow-y-auto';
  const panelContentClassName = isSlideOver
    ? 'flex flex-col gap-4 p-4 pb-8'
    : 'flex flex-col gap-4 p-4 h-full';
  const candidatesListClassName = isSlideOver
    ? 'flex flex-col gap-2 w-full mb-2'
    : 'flex flex-col gap-2 overflow-y-auto w-full mb-2';
  const missionDetailPageClassName = isSlideOver
    ? 'flex flex-col'
    : 'flex flex-col overflow-y-auto h-full';
  const tabsRowClassName = isSlideOver
    ? 'flex gap-2 w-full text-sm overflow-x-auto flex-nowrap'
    : 'flex gap-2 w-full text-sm';
  const subTabsRowClassName = isSlideOver
    ? 'flex gap-4 ft-sm overflow-x-auto flex-nowrap'
    : 'flex gap-4 ft-sm';
  const headerRowClassName = isSlideOver
    ? 'flex flex-col gap-3 sm:flex-row sm:gap-2 sm:justify-between sm:items-center'
    : 'flex gap-2 justify-between items-center';

  return (
    <>
      {isInfoMissionWaiting ? (
        <>
          <div
            className={`${panelClassName} animate-pulse`}
          >
            <div className={panelContentClassName}>
              <div className={'flex gap-1 justify-between items-center'}>
                <div className={'flex flex-col gap-2'}>
                  <div
                    className={
                      'flex gap-1 items-center font-black text-lg flex-1 min-w-0'
                    }
                  >
                    <div>
                      <div className={'h-6 bg-gray-200 rounded w-56'}></div>
                    </div>
                    <div>
                      <div className={'h-6 bg-gray-200 rounded w-8'}></div>
                    </div>
                  </div>
                  <div className={'text-sm text-gray-700'}>
                    <div className={'h-4 bg-gray-200 rounded w-36'}></div>
                  </div>
                </div>
                <div className={'flex gap-2'}>
                  <div className={'h-6 bg-gray-200 rounded w-6'}></div>
                  <div className={'h-6 bg-gray-200 rounded w-6'}></div>
                  <div className={'h-6 bg-gray-200 rounded w-6'}></div>
                </div>
              </div>
              <Divider />
              <div className={'flex gap-2 w-full text-sm'}>
                <div className={`cursor-pointer pt-1 pb-2 px-2`}>
                  <div className={'h-4 bg-gray-200 rounded w-24'}></div>
                </div>
                <div className={`cursor-pointer pt-1 pb-2 px-2`}>
                  <div className={'h-4 bg-gray-200 rounded w-24'}></div>
                </div>
                <div className={`cursor-pointer pt-1 pb-2 px-2`}>
                  <div className={'h-4 bg-gray-200 rounded w-24'}></div>
                </div>
              </div>
              <div className={'w-full'}>
                <div className={'flex gap-4 ft-sm'}>
                  <div className={`my-auto px-3 py-2 rounded cursor-pointer`}>
                    <div className={'flex gap-1'}>
                      <div className={'my-auto'}>
                        <div className={'h-4 bg-gray-200 rounded w-24'}></div>
                      </div>
                    </div>
                  </div>
                  <div className={`my-auto px-3 py-2 rounded cursor-pointer`}>
                    <div className={'flex gap-1'}>
                      <div className={'my-auto'}>
                        <div className={'h-4 bg-gray-200 rounded w-24'}></div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`my-auto text-gray-500 px-3 py-2 rounded cursor-pointer`}
                  >
                    <div className={'h-6 bg-gray-200 rounded w-6'}></div>
                  </div>
                  <div
                    className={`my-auto ml-auto px-3 py-2 rounded cursor-pointer`}
                  >
                    <div className={'flex gap-1'}>
                      <div className={'my-auto'}>
                        <div className={'h-4 bg-gray-200 rounded w-24'}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={'flex flex-col gap-2'}>
                <div className={'h-4 bg-gray-200 rounded w-16'}></div>
                <div className={'h-8 bg-gray-200 rounded w-full'}></div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {infoMission ? (
            <>
              <div className={panelClassName}>
                <div className={panelContentClassName}>
                  <div className={headerRowClassName}>
                    <div className={'flex flex-col min-w-0 flex-1'}>
                      <div
                        className={
                          'font-black text-lg'
                        }
                      >
                        {infoMission.title} n°{infoMission.id_mission}
                      </div>
                      <div className={'text-sm text-gray-700'}>
                        {infoMission.company?.name_commercial}
                        {' · '}
                        Créée par{' '}
                        {infoMission.creator
                          ? (infoMission.creator.firstname ||
                              infoMission.creator.lastname)
                            ? [infoMission.creator.firstname, infoMission.creator.lastname]
                                .filter(Boolean)
                                .join(' ')
                            : infoMission.creator.type === 'admin'
                              ? 'Jobbiz Sourcing'
                              : "l'entreprise"
                          : "l'entreprise"}
                        {formatDateCreation(infoMission.createdAt)}
                      </div>
                    </div>
                    <div className={`flex gap-2 flex-shrink-0 items-center ${isSlideOver ? 'flex-wrap' : ''}`}>
                      {(() => {
                        const currentStatus = infoMission.status || 'online';
                        const hasAssignedUser = !!infoMission.user_assigned;
                        const now = new Date();

                        const toYmd = (v) => {
                          if (!v) return null;
                          if (typeof v === 'string') return v.substring(0, 10);
                          if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().substring(0, 10);
                          return null;
                        };

                        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                        const startStr = toYmd(infoMission.duration?.datestart);
                        const endStr = toYmd(infoMission.duration?.dateend);
                        const hasValidRange = startStr != null && endStr != null;
                        const isInProgress = hasValidRange ? todayStr >= startStr && todayStr <= endStr : false;
                        const isUpcoming = hasValidRange ? todayStr < startStr : false;
                        const isPast = hasValidRange ? todayStr > endStr : false;

                        const statusConfig = {
                          online: { label: 'En ligne', bg: 'bg-green-100', text: 'text-green-800' },
                          suspended: { label: 'Suspendue', bg: 'bg-yellow-100', text: 'text-yellow-800' },
                          canceled: { label: 'Annulée', bg: 'bg-gray-100', text: 'text-gray-900' },
                          interrupted: { label: 'Interrompue', bg: 'bg-gray-100', text: 'text-gray-900' },
                        };

                        const isTerminal = ['canceled', 'interrupted'].includes(currentStatus);

                        if (isTerminal) {
                          const config = statusConfig[currentStatus];
                          return (
                            <div className={`${config.bg} ${config.text} ft-sm rounded-md flex items-center px-2 h-6`}>
                              {config.label}
                            </div>
                          );
                        }

                        if (hasAssignedUser) {
                          if (isPast) return null;

                          const handleBan = () => {
                            setObjID(infoMission?._id);
                            if (isUpcoming) toggle('mission_cancel');
                            else if (isInProgress) toggle('mission_interrupt');
                          };

                          return (
                            <Tooltip text={isUpcoming ? 'Annuler la mission' : 'Interrompre la mission'} placement="top">
                              <div>
                                <Button size={'SMI'} color={'DEFAULT'} onClick={handleBan}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                  </svg>
                                </Button>
                              </div>
                            </Tooltip>
                          );
                        }

                        const config = statusConfig[currentStatus] || statusConfig.online;
                        let options = [];

                        if (isUpcoming) {
                          options = [
                            { key: 'online', ...statusConfig.online },
                            { key: 'suspended', ...statusConfig.suspended },
                            { key: 'canceled', ...statusConfig.canceled },
                          ];
                        } else if (isInProgress) {
                          options = [
                            ...(currentStatus === 'suspended' ? [{ key: 'online', ...statusConfig.online }] : []),
                            { key: 'canceled', ...statusConfig.canceled },
                            { key: 'suspended', ...statusConfig.suspended },
                          ];
                        } else {
                          options = [
                            ...(currentStatus === 'suspended' ? [{ key: 'online', ...statusConfig.online }] : []),
                            { key: 'canceled', ...statusConfig.canceled },
                            { key: 'suspended', ...statusConfig.suspended },
                          ];
                        }

                        const canChangeStatus = options.length > 0;

                        const handleStatusSelect = (statusKey) => {
                          if (statusKey === currentStatus) return;
                          setObjID(infoMission?._id);
                          setDropdownStatusOpen(false);
                          if (statusKey === 'online') toggle('mission_resume');
                          else if (statusKey === 'suspended') toggle('mission_suspend');
                          else if (statusKey === 'canceled') toggle('mission_cancel');
                          else if (statusKey === 'interrupted') toggle('mission_interrupt');
                        };

                        return (
                          <div className="relative" ref={dropDownStatusRef}>
                            <div
                              className={`${config.bg} ${config.text} ft-sm rounded-md flex items-center gap-1 px-2 h-6 ${canChangeStatus ? 'cursor-pointer' : ''}`}
                              onClick={() => canChangeStatus && setDropdownStatusOpen(!dropdownStatusOpen)}
                            >
                              {config.label}
                              {canChangeStatus && (
                                <ChevronDown wh={14} color={currentStatus === 'suspended' ? '#6B3D0A' : '#065F46'} />
                              )}
                            </div>
                            {dropdownStatusOpen && canChangeStatus && (
                              <div className="absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg w-44 right-0 bg-gray-50 z-10">
                                {options
                                  .filter((opt) => opt.key !== currentStatus)
                                  .map((opt) => (
                                    <div
                                      key={opt.key}
                                      className="flex items-center gap-2 h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                                      onClick={() => handleStatusSelect(opt.key)}
                                    >
                                      {opt.label}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <CopyToClipboard
                        text={`${process.env.REACT_APP_INTERIM}ficheMissionSearch/${infoMission._id}`}
                      >
                        <Button size={'SMI'} color={'DEFAULT'}>
                          <LinkIcon wh={20} color={'#374151'} />
                        </Button>
                      </CopyToClipboard>
                      {['online', 'suspended'].includes(infoMission.status || 'online') && (
                        <div
                          className={'relative'}
                          ref={dropDownNotificationRef}
                          onClick={() => {
                            setDropdownNotificationOpen(true);
                          }}
                        >
                          <Button size={'SMI'} color={'PRIMARY'}>
                            <div className={'flex gap-1'}>
                              <BellIcon wh={20} color={'#FFFFFF'} />
                              Notifier
                            </div>
                          </Button>
                          {dropdownNotificationOpen && (
                            <div
                              className={
                                'absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg h-30 w-56 right-0 bg-gray-50'
                              }
                            >
                              <div
                                className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  setObjID(infoMission?._id);
                                  toggle('mission_notify');
                                }}
                              >
                                Notifier la mission
                              </div>
                              <div
                                className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  setObjID(infoMission?._id);
                                  toggle('mission_notify_global');
                                }}
                              >
                                Notification globale
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Divider />
                  {(infoMission.status === 'suspended') && (
                    <div className="rounded-lg p-3 bg-yellow-50 text-yellow-700 ft-sm mb-3">
                      Le recrutement de cette mission est suspendu, elle n'est plus visible par les candidats
                    </div>
                  )}
                  {(infoMission.status === 'canceled') && (
                    <div className="rounded-lg p-3 border border-gray-200 bg-gray-50 text-gray-700 ft-sm mb-3">
                      {infoMission.canceledAt &&
                      !Number.isNaN(new Date(infoMission.canceledAt).getTime()) ? (
                        <>
                          Cette mission est annulée le{' '}
                          {new Date(infoMission.canceledAt).toLocaleDateString('fr-FR')} à{' '}
                          {new Date(infoMission.canceledAt).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          pour le motif suivant : {missionStatusReasonLabel(infoMission.reason)}
                        </>
                      ) : (
                        <>
                          La mission a été annulée pour le motif suivant :{' '}
                          {missionStatusReasonLabel(infoMission.reason)}
                        </>
                      )}
                    </div>
                  )}
                  {(infoMission.status === 'interrupted') && (
                    <div className="rounded-lg p-3 border border-gray-200 bg-gray-50 text-gray-700 ft-sm mb-3">
                      Cette mission est interrompue le{' '}
                      {infoMission.updatedAt
                        ? new Date(infoMission.updatedAt).toLocaleDateString('fr-FR')
                        : '—'}{' '}
                      à{' '}
                      {infoMission.updatedAt
                        ? new Date(infoMission.updatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : '—'}{' '}
                      pour le motif suivant : {missionStatusReasonLabel(infoMission.reason)}
                      {infoMission.replacement_mission_info && (
                        <>
                          <br />
                          Mission de remplacement :{' '}
                          <ReplacementMissionLink
                            replacement={infoMission.replacement_mission_info}
                            onOpen={openReplacementMission}
                          />
                        </>
                      )}
                    </div>
                  )}
                  {/* Duplicated without being interrupted — « Nouvelle mission
                      requise » leaves this one online: it named its replacement
                      nowhere. */}
                  {infoMission.status !== 'interrupted' &&
                    infoMission.replacement_mission_info && (
                      <div className="rounded-lg p-3 border border-gray-200 bg-gray-50 text-gray-700 ft-sm mb-3">
                        Mission de remplacement créée :{' '}
                        <ReplacementMissionLink
                          replacement={infoMission.replacement_mission_info}
                          onOpen={openReplacementMission}
                        />
                      </div>
                    )}
                  {(role === 500 || role === 515) && (
                    <>
                          <div className={tabsRowClassName}>
                            <div
                              className={`cursor-pointer pt-1 pb-2 px-2 border-b ${
                                missionPage === 1
                                  ? 'border-yellow-500 text-yellow-500'
                                  : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
                              }`}
                              onClick={() => setMissionPage(1)}
                            >
                              {infoMission.user_assigned
                                ? 'Historique de candidature'
                                : 'Candidature'}
                            </div>
                            <div
                              className={`cursor-pointer pt-1 pb-2 px-2 border-b ${
                                missionPage === 2
                                  ? 'border-yellow-500 text-yellow-500'
                                  : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
                              }`}
                              onClick={() => setMissionPage(2)}
                            >
                              Contrats
                            </div>
                            <div
                              className={`cursor-pointer pt-1 pb-2 px-2 border-b ${
                                missionPage === 3
                                  ? 'border-yellow-500 text-yellow-500'
                                  : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
                              }`}
                              onClick={() => setMissionPage(3)}
                            >
                              Détail de mission
                            </div>
                            <div
                              className={`cursor-pointer pt-1 pb-2 px-2 border-b ${
                                missionPage === 4
                                  ? 'border-yellow-500 text-yellow-500'
                                  : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
                              }`}
                              onClick={() => setMissionPage(4)}
                            >
                              Activité
                            </div>
                          </div>
                          {missionPage === 1 && (
                            <>
                              <div className={'w-full'}>
                                <div className={subTabsRowClassName}>
                                  <div
                                    className={`my-auto px-3 py-2 rounded cursor-pointer ${
                                      typeSearchTempWorkerAssignation === 1
                                        ? 'text-gray-700 bg-gray-100'
                                        : 'text-gray-500'
                                    }`}
                                    onClick={() =>
                                      setTypeSearchTempWorkerAssignation(1)
                                    }
                                  >
                                    <div className={'flex gap-1'}>
                                      <div className={'my-auto'}>
                                        Candidature
                                      </div>
                                      <div
                                        className={
                                          'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
                                        }
                                      >
                                        {countTempWorkerPostulate}
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className={`my-auto px-3 py-2 rounded cursor-pointer ${
                                      typeSearchTempWorkerAssignation === 5
                                        ? 'text-gray-700 bg-gray-100'
                                        : 'text-gray-500'
                                    }`}
                                    onClick={() =>
                                      setTypeSearchTempWorkerAssignation(5)
                                    }
                                  >
                                    <div className={'flex gap-1'}>
                                      <div className={'my-auto'}>En attente</div>
                                      <div
                                        className={
                                          'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
                                        }
                                      >
                                        {countTempWorkerPendingContact}
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className={`my-auto px-3 py-2 rounded cursor-pointer ${
                                      typeSearchTempWorkerAssignation === 2
                                        ? 'text-gray-700 bg-gray-100'
                                        : 'text-gray-500'
                                    }`}
                                    onClick={() =>
                                      setTypeSearchTempWorkerAssignation(2)
                                    }
                                  >
                                    <div className={'flex gap-1'}>
                                      <div className={'my-auto'}>Refusé</div>
                                      <div
                                        className={
                                          'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
                                        }
                                      >
                                        {countTempWorkerRefused}
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className={`my-auto text-gray-500 px-3 py-2 rounded cursor-pointer ${
                                      typeSearchTempWorkerAssignation === 3
                                        ? 'text-gray-700 bg-gray-100'
                                        : 'text-gray-500'
                                    }`}
                                    onClick={() =>
                                      setTypeSearchTempWorkerAssignation(3)
                                    }
                                  >
                                    <SearchLoop wh={20} color={'#6B7280'} />
                                  </div>
                                  <div
                                    className={`my-auto ml-auto px-3 py-2 rounded cursor-pointer ${
                                      typeSearchTempWorkerAssignation === 4
                                        ? 'text-gray-700 bg-gray-100'
                                        : 'text-gray-500'
                                    }`}
                                    onClick={() =>
                                      setTypeSearchTempWorkerAssignation(4)
                                    }
                                  >
                                    <div className={'flex gap-1'}>
                                      <div className={'my-auto'}>
                                        Invitation
                                      </div>
                                      <div
                                        className={
                                          'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
                                        }
                                      >
                                        {countUsersInvite}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/*Postulate search*/}
                              {typeSearchTempWorkerAssignation === 1 && (
                                <>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <SearchLoop wh={16} color="#9CA3AF" />
                                    </div>
                                    <input
                                      type="text"
                                      className="w-full h-10 pl-9 pr-8 ft-b border rounded-lg text-sm"
                                      placeholder="Intérimaire"
                                      value={searchTempWorkerAssignation}
                                      onChange={(e) =>
                                        setSearchTempWorkerAssignation(
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  {searchTempWorkerAssignationResultLoadData ? (
                                    <>
                                      {searchTempWorkerAssignationResultWaiting ? (
                                        <div
                                          className={
                                            'flex flex-col animate-pulse'
                                          }
                                        >
                                          {[1, 2, 3, 4, 5].map((i, index) => (
                                            <div
                                              key={index}
                                              className={
                                                'w-full flex flex-col gap-5 px-2 py-4 border-b cursor-pointer'
                                              }
                                            >
                                              <div
                                                className={
                                                  'flex flex-col gap-2'
                                                }
                                              >
                                                <div
                                                  className={'text-gray-500'}
                                                >
                                                  <div
                                                    className={
                                                      'flex flex-col gap-3'
                                                    }
                                                  >
                                                    <div
                                                      className={
                                                        'w-full text-sm'
                                                      }
                                                    >
                                                      <div
                                                        className={
                                                          'flex flex-col gap-1'
                                                        }
                                                      >
                                                        <div
                                                          className={
                                                            'flex gap-1 items-center w-full flex-1 min-w-0'
                                                          }
                                                        >
                                                          <div
                                                            className={
                                                              'text-base text-gray-900 truncate'
                                                            }
                                                          >
                                                            <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                          </div>
                                                        </div>
                                                        <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                        <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                      </div>
                                                    </div>
                                                    <div
                                                      className={'flex gap-1'}
                                                    >
                                                      <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                      <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <>
                                          {searchTempWorkerAssignationToDisplay ? (
                                            <>
                                              <div
                                                className={
                                                  candidatesListClassName
                                                }
                                              >
                                                {searchTempWorkerAssignationToDisplay
                                                  .complete?.length > 0 ||
                                                searchTempWorkerAssignationToDisplay
                                                  .partial?.length > 0 ||
                                                searchTempWorkerAssignationToDisplay
                                                  .not_updated?.length > 0 ||
                                                searchTempWorkerAssignationToDisplay
                                                  .incomplete?.length > 0 ||
                                                searchTempWorkerAssignationToDisplay
                                                  .signup_pending?.length > 0 ? (
                                                  <>
                                                    {searchTempWorkerAssignationToDisplay
                                                      .complete.length > 0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultCompleteOpen(
                                                              !isTempWorkerResultCompleteOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Disponibilité complète</span>
                                                          <div className={`transition-transform ${isTempWorkerResultCompleteOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultCompleteOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerAssignationToDisplay.complete?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerAssignationToDisplay.complete.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickRefused:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        refuseTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickPendingContact:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        pendingContactTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerAssignationToDisplay
                                                      .partial.length > 0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultPartialOpen(
                                                              !isTempWorkerResultPartialOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Disponibilité partielle</span>
                                                          <div className={`transition-transform ${isTempWorkerResultPartialOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultPartialOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerAssignationToDisplay.partial?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerAssignationToDisplay.partial.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickRefused:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        refuseTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickPendingContact:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        pendingContactTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerAssignationToDisplay
                                                      .not_updated.length >
                                                      0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultNotUpdatedOpen(
                                                              !isTempWorkerResultNotUpdatedOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Non mis à jour</span>
                                                          <div className={`transition-transform ${isTempWorkerResultNotUpdatedOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultNotUpdatedOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerAssignationToDisplay.not_updated?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerAssignationToDisplay.not_updated.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickRefused:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        refuseTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickPendingContact:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        pendingContactTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerAssignationToDisplay
                                                      .incomplete.length >
                                                      0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultIncompleteOpen(
                                                              !isTempWorkerResultIncompleteOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Non disponible</span>
                                                          <div className={`transition-transform ${isTempWorkerResultIncompleteOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultIncompleteOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerAssignationToDisplay.incomplete?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerAssignationToDisplay.incomplete.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickRefused:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        refuseTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickPendingContact:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        pendingContactTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerAssignationToDisplay
                                                      .signup_pending.length >
                                                      0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultSignupPendingOpen(
                                                              !isTempWorkerResultSignupPendingOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Inscription non finalisée</span>
                                                          <div className={`transition-transform ${isTempWorkerResultSignupPendingOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultSignupPendingOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerAssignationToDisplay.signup_pending?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerAssignationToDisplay.signup_pending.length - 1}
                                                                  inSignupPendingGroup
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickRefused:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        refuseTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                    onClickPendingContact:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        pendingContactTempWorker(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                  </>
                                                ) : (
                                                  <EmptyStateMission type="candidature" />
                                                )}
                                              </div>
                                              <div className={''}>
                                                <Pagination
                                                  currentPage={
                                                    currentPageTempWorkerAssignation
                                                  }
                                                  countPerPage={
                                                    limitElementTempWorkerAssignation
                                                  }
                                                  countTotal={
                                                    countTotalTempWorkerAssignation
                                                  }
                                                  totalPages={
                                                    totalPagesTempWorkerAssignation
                                                  }
                                                  setCurrentPage={
                                                    setCurrentPageTempWorkerAssignation
                                                  }
                                                />
                                              </div>
                                            </>
                                          ) : (
                                            <EmptyStateMission type="candidature" />
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>la</>
                                  )}
                                </>
                              )}

                              {/*Postulate Refused search*/}
                              {typeSearchTempWorkerAssignation === 2 && (
                                <>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <SearchLoop wh={16} color="#9CA3AF" />
                                    </div>
                                    <input
                                      type="text"
                                      className="w-full h-10 pl-9 pr-8 ft-b border rounded-lg text-sm"
                                      placeholder="Intérimaire"
                                      value={searchTempWorkerAssignationRefused}
                                      onChange={(e) =>
                                        setSearchTempWorkerAssignationRefused(
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  {searchTempWorkerAssignationRefusedResultLoadData ? (
                                    <>
                                      {searchTempWorkerAssignationRefusedResultWaiting ? (
                                        <div
                                          className={
                                            'flex flex-col animate-pulse'
                                          }
                                        >
                                          {[1, 2, 3, 4, 5].map((i, index) => (
                                            <div
                                              key={index}
                                              className={
                                                'w-full flex flex-col gap-5 px-2 py-4 border-b cursor-pointer'
                                              }
                                            >
                                              <div
                                                className={
                                                  'flex flex-col gap-2'
                                                }
                                              >
                                                <div
                                                  className={'text-gray-500'}
                                                >
                                                  <div
                                                    className={
                                                      'flex flex-col gap-3'
                                                    }
                                                  >
                                                    <div
                                                      className={
                                                        'w-full text-sm'
                                                      }
                                                    >
                                                      <div
                                                        className={
                                                          'flex flex-col gap-1'
                                                        }
                                                      >
                                                        <div
                                                          className={
                                                            'flex gap-1 items-center w-full flex-1 min-w-0'
                                                          }
                                                        >
                                                          <div
                                                            className={
                                                              'text-base text-gray-900 truncate'
                                                            }
                                                          >
                                                            <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                          </div>
                                                        </div>
                                                        <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                        <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                      </div>
                                                    </div>
                                                    <div
                                                      className={'flex gap-1'}
                                                    >
                                                      <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                      <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <>
                                          {searchTempWorkerAssignationRefusedToDisplay ? (
                                            <>
                                              <div
                                                className={
                                                  candidatesListClassName
                                                }
                                              >
                                                {Object.keys(searchTempWorkerAssignationRefusedToDisplay).length > 0 ? (
                                                  <>
                                                    {Object.entries(searchTempWorkerAssignationRefusedToDisplay).map(([cat, workers]) => {
                                                      const categoryLabels = {
                                                        profil: 'Profil',
                                                        experience: 'Expérience',
                                                        disponibilite: 'Disponibilité',
                                                        comportement: 'Comportement',
                                                        autre: 'Autre',
                                                        sans_motif: 'Sans motif',
                                                      };
                                                      const label = categoryLabels[cat] || cat;
                                                      const isOpen = openRefusedCategoryAccordions[cat] !== false;
                                                      return (
                                                        <div key={cat} className="rounded-md border border-gray-200 mt-1">
                                                          <div
                                                            className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                            onClick={() =>
                                                              setOpenRefusedCategoryAccordions(prev => ({
                                                                ...prev,
                                                                [cat]: prev[cat] === undefined ? false : !prev[cat]
                                                              }))
                                                            }
                                                          >
                                                            <span className="text-sm font-semibold text-gray-900">{label}</span>
                                                            <div className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}>
                                                              <ChevronRight wh={16} color="#4B5563" />
                                                            </div>
                                                          </div>
                                                          {isOpen && (
                                                            <div className="border-t border-gray-100">
                                                              {workers.map((tempWorker, index) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={tempWorker}
                                                                  selectedTempWorkerAssignation={selectedTempWorkerAssignation}
                                                                  setSelectedTempWorkerAssignation={setSelectedTempWorkerAssignation}
                                                                  selectedTempWorkerRefused={selectedTempWorkerRefused}
                                                                  setSelectedTempWorkerRefused={setSelectedTempWorkerRefused}
                                                                  missionDateStart={missionDateStart}
                                                                  missionDateEnd={missionDateEnd}
                                                                  isRefused
                                                                  isLast={index === workers.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation: (tempWorkerId) =>
                                                                      assignationMissionSubmit(tempWorkerId),
                                                                    onClickRefused: (tempWorkerId) =>
                                                                      refuseTempWorker(tempWorkerId),
                                                                  })}
                                                                />
                                                              ))}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </>
                                                ) : (
                                                  <EmptyStateMission type="refus" />
                                                )}
                                              </div>
                                              <div className={''}>
                                                <Pagination
                                                  currentPage={
                                                    currentPageTempWorkerAssignationRefused
                                                  }
                                                  countPerPage={
                                                    limitElementTempWorkerAssignationRefused
                                                  }
                                                  countTotal={
                                                    countTotalTempWorkerAssignationRefused
                                                  }
                                                  totalPages={
                                                    totalPagesTempWorkerAssignationRefused
                                                  }
                                                  setCurrentPage={
                                                    setCurrentPageTempWorkerAssignationRefused
                                                  }
                                                />
                                              </div>
                                            </>
                                          ) : (
                                            <EmptyStateMission type="refus" />
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>la</>
                                  )}
                                </>
                              )}

                              {/*Pending contact search*/}
                              {typeSearchTempWorkerAssignation === 5 && (
                                <>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <SearchLoop wh={16} color="#9CA3AF" />
                                    </div>
                                    <input
                                      type="text"
                                      className="w-full h-10 pl-9 pr-8 ft-b border rounded-lg text-sm"
                                      placeholder="Intérimaire"
                                      value={searchTempWorkerAssignationPendingContact}
                                      onChange={(e) =>
                                        setSearchTempWorkerAssignationPendingContact(
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  {searchTempWorkerAssignationPendingContactResultWaiting ? (
                                    <div className={'flex flex-col animate-pulse'}>
                                      {[1, 2, 3].map((i, index) => (
                                        <div key={index} className={'w-full flex flex-col gap-5 px-2 py-4 border-b cursor-pointer'}>
                                          <div className={'flex flex-col gap-2'}>
                                            <div className={'text-gray-500'}>
                                              <div className={'flex flex-col gap-3'}>
                                                <div className={'w-full text-sm'}>
                                                  <div className={'flex flex-col gap-1'}>
                                                    <div className={'flex gap-1 items-center w-full flex-1 min-w-0'}>
                                                      <div className={'text-base text-gray-900 truncate'}>
                                                        <div className={'w-32 h-4 bg-gray-200 rounded'}></div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <>
                                      {searchTempWorkerAssignationPendingContactToDisplay &&
                                      (searchTempWorkerAssignationPendingContactToDisplay.complete?.length > 0 ||
                                        searchTempWorkerAssignationPendingContactToDisplay.partial?.length > 0 ||
                                        searchTempWorkerAssignationPendingContactToDisplay.not_updated?.length > 0 ||
                                        searchTempWorkerAssignationPendingContactToDisplay.incomplete?.length > 0 ||
                                        searchTempWorkerAssignationPendingContactToDisplay.signup_pending?.length > 0) ? (
                                        <>
                                        <div className="flex flex-col gap-1">
                                          {['complete', 'partial', 'not_updated', 'incomplete', 'signup_pending'].map((category) => {
                                            const items = searchTempWorkerAssignationPendingContactToDisplay[category];
                                            if (!items || items.length === 0) return null;
                                            const labels = {
                                              complete: 'Disponibilité complète',
                                              partial: 'Disponibilité partielle',
                                              not_updated: 'Non mis à jour',
                                              incomplete: 'Profil incomplet',
                                              signup_pending: 'Inscription non finalisée',
                                            };
                                            return (
                                              <div key={category} className="rounded-md border border-gray-200 mt-1">
                                                <div className="border-t border-gray-100">
                                                  {items.map((tempWorker, index) => (
                                                    <MissionDetailTempWorker
                                                      key={tempWorker._id || index}
                                                      index={index}
                                                      candidatesList={activeTabCandidateIds}
                                                      tempWorker={tempWorker}
                                                      missionDateStart={missionDateStart}
                                                      missionDateEnd={missionDateEnd}
                                                      isPendingContact={true}
                                                      isLast={index === items.length - 1}
                                                      inSignupPendingGroup={category === 'signup_pending'}
                                                      {...(!infoMission.user_assigned && {
                                                        onClickAssignation: (tempWorkerId) =>
                                                          assignationMissionSubmit(tempWorkerId),
                                                        onClickRefused: (tempWorkerId) =>
                                                          refuseTempWorker(tempWorkerId),
                                                        onClickPendingContact: (tempWorkerId) =>
                                                          pendingContactTempWorker(tempWorkerId),
                                                      })}
                                                    />
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <div className={''}>
                                          <Pagination
                                            currentPage={
                                              currentPageTempWorkerAssignationPendingContact
                                            }
                                            countPerPage={
                                              limitElementTempWorkerAssignationPendingContact
                                            }
                                            countTotal={
                                              countTotalTempWorkerAssignationPendingContact
                                            }
                                            totalPages={
                                              totalPagesTempWorkerAssignationPendingContact
                                            }
                                            setCurrentPage={
                                              setCurrentPageTempWorkerAssignationPendingContact
                                            }
                                          />
                                        </div>
                                        </>
                                      ) : (
                                        <EmptyStateMission type="pendingContact" />
                                      )}
                                    </>
                                  )}
                                </>
                              )}

                              {/*Candidate search*/}
                              {typeSearchTempWorkerAssignation === 3 && (
                                <>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <SearchLoop wh={16} color="#9CA3AF" />
                                    </div>
                                    <input
                                      type="text"
                                      className="w-full h-10 pl-9 pr-8 ft-b border rounded-lg text-sm"
                                      placeholder="Intérimaire"
                                      value={searchTempWorker}
                                      onChange={(e) =>
                                        setSearchTempWorker(e.target.value)
                                      }
                                    />
                                  </div>
                                  {searchTempWorkerResultLoadData ? (
                                    <>
                                      {searchTempWorkerResultWaiting ? (
                                        <>
                                          <div
                                            className={
                                              'flex flex-col animate-pulse'
                                            }
                                          >
                                            {[1, 2, 3, 4, 5].map((i, index) => (
                                              <div
                                                key={index}
                                                className={
                                                  'w-full flex flex-col gap-5 px-2 py-4 border-b cursor-pointer'
                                                }
                                              >
                                                <div
                                                  className={
                                                    'flex flex-col gap-2'
                                                  }
                                                >
                                                  <div
                                                    className={'text-gray-500'}
                                                  >
                                                    <div
                                                      className={
                                                        'flex flex-col gap-3'
                                                      }
                                                    >
                                                      <div
                                                        className={
                                                          'w-full text-sm'
                                                        }
                                                      >
                                                        <div
                                                          className={
                                                            'flex flex-col gap-1'
                                                          }
                                                        >
                                                          <div
                                                            className={
                                                              'flex gap-1 items-center w-full flex-1 min-w-0'
                                                            }
                                                          >
                                                            <div
                                                              className={
                                                                'text-base text-gray-900 truncate'
                                                              }
                                                            >
                                                              <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                            </div>
                                                          </div>
                                                          <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                          <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                        </div>
                                                      </div>
                                                      <div
                                                        className={'flex gap-1'}
                                                      >
                                                        <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                        <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          {searchTempWorkerResult ? (
                                            <>
                                              <div
                                                className={
                                                  candidatesListClassName
                                                }
                                              >
                                                {searchTempWorkerResult.complete
                                                  ?.length > 0 ||
                                                searchTempWorkerResult.partial
                                                  ?.length > 0 ||
                                                searchTempWorkerResult
                                                  .not_updated?.length > 0 ||
                                                searchTempWorkerResult
                                                  .incomplete?.length > 0 ? (
                                                  <>
                                                    {searchTempWorkerResult
                                                      .complete.length > 0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultCompleteOpen(
                                                              !isTempWorkerResultCompleteOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Disponibilité complète</span>
                                                          <div className={`transition-transform ${isTempWorkerResultCompleteOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultCompleteOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerResult.complete?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerResult.complete.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerResult
                                                      .partial.length > 0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultPartialOpen(
                                                              !isTempWorkerResultPartialOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Disponibilité partielle</span>
                                                          <div className={`transition-transform ${isTempWorkerResultPartialOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultPartialOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerResult.partial?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerResult.partial.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerResult
                                                      .not_updated.length >
                                                      0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultNotUpdatedOpen(
                                                              !isTempWorkerResultNotUpdatedOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Non mis à jour</span>
                                                          <div className={`transition-transform ${isTempWorkerResultNotUpdatedOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultNotUpdatedOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerResult.not_updated?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerResult.not_updated.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {searchTempWorkerResult
                                                      .incomplete.length >
                                                      0 && (
                                                      <div className="rounded-md border border-gray-200 mt-1">
                                                        <div
                                                          className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                          onClick={() =>
                                                            setIsTempWorkerResultIncompleteOpen(
                                                              !isTempWorkerResultIncompleteOpen
                                                            )
                                                          }
                                                        >
                                                          <span className="text-sm font-semibold text-gray-900">Non disponible</span>
                                                          <div className={`transition-transform ${isTempWorkerResultIncompleteOpen ? 'rotate-90' : ''}`}>
                                                            <ChevronRight wh={16} color="#4B5563" />
                                                          </div>
                                                        </div>
                                                        {isTempWorkerResultIncompleteOpen && (
                                                          <div className="border-t border-gray-100">
                                                            {                                                            searchTempWorkerResult.incomplete?.map(
                                                              (
                                                                tempWorker,
                                                                index
                                                              ) => (
                                                                <MissionDetailTempWorker
                                                                  key={tempWorker._id || index}
                                                                  index={index}
                                                                  candidatesList={activeTabCandidateIds}
                                                                  tempWorker={
                                                                    tempWorker
                                                                  }
                                                                  selectedTempWorkerAssignation={
                                                                    selectedTempWorkerAssignation
                                                                  }
                                                                  setSelectedTempWorkerAssignation={
                                                                    setSelectedTempWorkerAssignation
                                                                  }
                                                                  selectedTempWorkerRefused={
                                                                    selectedTempWorkerRefused
                                                                  }
                                                                  setSelectedTempWorkerRefused={
                                                                    setSelectedTempWorkerRefused
                                                                  }
                                                                  missionDateStart={
                                                                    missionDateStart
                                                                  }
                                                                  missionDateEnd={
                                                                    missionDateEnd
                                                                  }
                                                                  isLast={index === searchTempWorkerResult.incomplete.length - 1}
                                                                  {...(!infoMission.user_assigned && {
                                                                    onClickAssignation:
                                                                      (
                                                                        tempWorkerId
                                                                      ) =>
                                                                        assignationMissionSubmit(
                                                                          tempWorkerId
                                                                        ),
                                                                  })}
                                                                />
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                  </>
                                                ) : (
                                                  <EmptyStateMission type="recherche" />
                                                )}
                                              </div>
                                              <div className={''}>
                                                <Pagination
                                                  currentPage={
                                                    currentPageTempWorker
                                                  }
                                                  countPerPage={
                                                    limitElementTempWorker
                                                  }
                                                  countTotal={
                                                    countTotalTempWorker
                                                  }
                                                  totalPages={
                                                    totalPagesTempWorker
                                                  }
                                                  setCurrentPage={
                                                    setCurrentPageTempWorker
                                                  }
                                                />
                                              </div>
                                            </>
                                          ) : (
                                            <EmptyStateMission type="recherche" />
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>la</>
                                  )}
                                </>
                              )}
                              {/*Invite search*/}
                              {typeSearchTempWorkerAssignation === 4 && (
                                <>
                                  <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <SearchLoop wh={16} color="#9CA3AF" />
                                      </div>
                                      <input
                                        type="text"
                                        className="w-full h-10 pl-9 pr-8 ft-b border rounded-lg text-sm"
                                        placeholder="Intérimaire"
                                        value={searchTempWorkerInvite}
                                        onChange={(e) =>
                                          setSearchTempWorkerInvite(e.target.value)
                                        }
                                      />
                                    </div>
                                    <Button
                                      size={'SMI'}
                                      color={'SECONDARY'}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setObjID(infoMission?._id);
                                        toggle('mission_invite_candidates');
                                      }}
                                    >
                                      <div className="flex items-center gap-1">
                                        <Plus wh={20} color={'#374151'} />
                                        Ajouter
                                      </div>
                                    </Button>
                                  </div>
                                  {searchTempWorkerInviteResultWaiting ? (
                                    <div
                                      className={'flex flex-col animate-pulse'}
                                    >
                                      {[1, 2, 3, 4, 5].map((i, index) => (
                                        <div
                                          key={index}
                                          className={
                                            'w-full flex flex-col gap-5 px-2 py-4 border-b cursor-pointer'
                                          }
                                        >
                                          <div
                                            className={'flex flex-col gap-2'}
                                          >
                                            <div className={'text-gray-500'}>
                                              <div
                                                className={
                                                  'flex flex-col gap-3'
                                                }
                                              >
                                                <div
                                                  className={'w-full text-sm'}
                                                >
                                                  <div
                                                    className={
                                                      'flex flex-col gap-1'
                                                    }
                                                  >
                                                    <div
                                                      className={
                                                        'flex gap-1 items-center w-full flex-1 min-w-0'
                                                      }
                                                    >
                                                      <div
                                                        className={
                                                          'text-base text-gray-900 truncate'
                                                        }
                                                      >
                                                        <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                      </div>
                                                    </div>
                                                    <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                    <div className="w-56 h-5 bg-gray-200 rounded"></div>
                                                  </div>
                                                </div>
                                                <div className={'flex gap-1'}>
                                                  <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                  <div className="w-12 h-5 bg-gray-200 rounded"></div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <>
                                      {searchTempWorkerInviteResult.length > 0 ? (
                                        <div className={candidatesListClassName}>
                                          {(() => {
                                            // "taken" is written by the server when the mission goes to
                                            // someone else — the invited profile did nothing. Grouping it
                                            // under "Accepté" made it read as an acceptance on their part.
                                            const grouped = {
                                              refused: searchTempWorkerInviteResult.filter(u => u.status === 'refused'),
                                              accepted: searchTempWorkerInviteResult.filter(u => u.status === 'accepted'),
                                              taken: searchTempWorkerInviteResult.filter(u => u.status === 'taken'),
                                              pending: searchTempWorkerInviteResult.filter(u => u.status === 'pending'),
                                            };
                                            const hasFilteredResults = Object.values(grouped).some(items => items.length > 0);
                                            if (!hasFilteredResults) {
                                              return <EmptyStateMission type="recherche" />;
                                            }
                                            const accordions = [
                                              { key: 'refused', label: 'Refusé', items: grouped.refused },
                                              { key: 'accepted', label: 'Accepté', items: grouped.accepted },
                                              { key: 'taken', label: 'Attribuée à un autre profil', items: grouped.taken },
                                              { key: 'pending', label: 'En attente', items: grouped.pending },
                                            ];
                                            return accordions.map(({ key, label, items }) => {
                                              if (items.length === 0) return null;
                                              const isOpen = openInvitationAccordions[key] !== false;
                                              return (
                                                <div key={key} className="rounded-md border border-gray-200 mt-1">
                                                  <div
                                                    className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                                                    onClick={() => setOpenInvitationAccordions(prev => ({ ...prev, [key]: prev[key] === undefined ? false : !prev[key] }))}
                                                  >
                                                    <span className="text-sm font-semibold text-gray-900">{label}</span>
                                                    <div className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}>
                                                      <ChevronRight wh={16} color="#4B5563" />
                                                    </div>
                                                  </div>
                                                  {isOpen && (
                                                    <div className="border-t border-gray-100">
                                                      {items.map((tempWorkerInvite, index) => (
                                                        <div
                                                          key={tempWorkerInvite._id || index}
                                                          className="border-b border-gray-100 last:border-b-0 p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                                                          onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const idx = activeTabCandidateIds.indexOf(tempWorkerInvite._id);
                                                            setSlideOverCandidatesList(activeTabCandidateIds);
                                                            setSlideOverCurrentIndex(idx >= 0 ? idx : 0);
                                                            openTempWorkerInfo(tempWorkerInvite._id);
                                                          }}
                                                        >
                                                          <div className="flex justify-between items-center text-sm">
                                                            <div className="">
                                                              <TempWorkerMission user={tempWorkerInvite} />
                                                            </div>
                                                            <div className="">
                                                              {tempWorkerInvite.status === 'pending' && <Badge type="WARNING">En attente</Badge>}
                                                              {tempWorkerInvite.status === 'accepted' && <Badge type="VALID">Accepté</Badge>}
                                                              {tempWorkerInvite.status === 'refused' && <Badge type="ALERT">Refusé</Badge>}
                                                              {tempWorkerInvite.status === 'taken' && <Badge type="ALERT">Pourvue</Badge>}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            });
                                          })()}
                                        </div>
                                      ) : (
                                        <EmptyStateMission type="invitation" />
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          {missionPage === 2 && (
                            <div className={'flex flex-col gap-6'}>
                              <div className={'flex flex-col gap-3'}>
                                {infoMission.user_assigned && (
                                  <div className="text-sm font-semibold text-gray-900">Profil attribué</div>
                                )}
                                {infoMission.user_assigned ? (
                                  <>
                                    <div
                                      className={
                                        'flex gap-4 justify-between items-center border rounded-md px-4 py-2.5 text-sm text-gray-500 cursor-pointer'
                                      }
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openTempWorkerInfo(
                                          infoMission.user_assigned
                                        );
                                      }}
                                    >
                                      <TempWorkerMission
                                        user={infoMission.user_assigned_infos}
                                      />
                                      {(() => {
                                        const now = new Date();
                                        const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                                        const endYmd = infoMission.duration?.dateend ? String(infoMission.duration.dateend).substring(0, 10) : null;
                                        const startYmd = infoMission.duration?.datestart ? String(infoMission.duration.datestart).substring(0, 10) : null;
                                        const isPastAssigned = endYmd ? todayYmd > endYmd : false;
                                        const isTerminalAssigned = ['canceled', 'interrupted'].includes(infoMission.status);
                                        if (isPastAssigned || isTerminalAssigned) return null;
                                        return (
                                          <Tooltip text="Dés-attribuer" placement="top">
                                            <div>
                                              <Button
                                                size={'SMI'}
                                                color={'DEFAULT'}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setObjID(infoMission?._id);
                                                  if (startYmd && todayYmd < startYmd) toggle('mission_cancel');
                                                  else toggle('mission_interrupt');
                                                }}
                                              >
                                                <Close wh={20} color={'#374151'} />
                                              </Button>
                                            </div>
                                          </Tooltip>
                                        );
                                      })()}
                                    </div>
                                    {infoMission.user_assigned_infos
                                      ?.tempworker?.identity && (
                                      <div
                                        className={
                                          'grid gap-2 grid-cols-1 md:grid-cols-2 text-sm'
                                        }
                                      >
                                        {/* Identité */}
                                        <IdentityFileItem
                                          title={identityTitle}
                                          isOpen={isIdentityFileOpen.identity}
                                          onToggle={() => {
                                            setIsIdentityFileOpen(
                                              (prevData) => ({
                                                ...prevData,
                                                identity: !prevData.identity,
                                              })
                                            );
                                          }}
                                          openDocument={openDocument}
                                          id="identity"
                                          validatedType={identityValidatedType}
                                          details={identityDetails}
                                        />
                                        {/* Selfie */}
                                        <IdentityFileItem
                                          title={selfieTitle}
                                          isOpen={isIdentityFileOpen.selfie}
                                          onToggle={() => {
                                            setIsIdentityFileOpen(
                                              (prevData) => ({
                                                ...prevData,
                                                selfie: !prevData.selfie,
                                              })
                                            );
                                          }}
                                          openDocument={openDocument}
                                          id="selfie"
                                          validatedType={selfieValidatedType}
                                          details={selfieDetails}
                                        />
                                        {/* Visa */}
                                        {infoMission.user_assigned_infos
                                          .tempworker.visa?.type && (
                                          <IdentityFileItem
                                            title={visaTitle}
                                            isOpen={isIdentityFileOpen.visa}
                                            onToggle={() => {
                                              setIsIdentityFileOpen(
                                                (prevData) => ({
                                                  ...prevData,
                                                  visa: !prevData.visa,
                                                })
                                              );
                                            }}
                                            openDocument={openDocument}
                                            id="visa"
                                            validatedType={visaValidatedType}
                                            details={visaDetails}
                                          />
                                        )}
                                        {/* Carte Vitale */}
                                        <IdentityFileItem
                                          title={cardVitalityTitle}
                                          isOpen={
                                            isIdentityFileOpen.card_vitality
                                          }
                                          onToggle={() => {
                                            setIsIdentityFileOpen(
                                              (prevData) => ({
                                                ...prevData,
                                                card_vitality:
                                                  !prevData.card_vitality,
                                              })
                                            );
                                          }}
                                          openDocument={openDocument}
                                          id="card_vitality"
                                          validatedType={
                                            cardVitalityValidatedType
                                          }
                                          details={cardVitalityDetails}
                                        />
                                        {/* Justificatif d'adresse */}
                                        <IdentityFileItem
                                          title={proofOfAddressTitle}
                                          openDocument={openDocument}
                                          id="proof_of_address"
                                          validatedType={
                                            proofOfAddressValidatedType
                                          }
                                          details={[]}
                                        />
                                        {/* Attestation d'hébergement */}
                                        {infoMission.user_assigned_infos
                                          .tempworker?.accommodation_of_address
                                          ?.validated !== '0' && (
                                          <IdentityFileItem
                                            title={accommodationTitle}
                                            openDocument={openDocument}
                                            id="accommodation_of_address"
                                            validatedType={
                                              accommodationValidatedType
                                            }
                                            details={[]}
                                          />
                                        )}
                                        {/* Pièce d'identité de l'hôte */}
                                        {infoMission.user_assigned_infos
                                          .tempworker?.hostIdentity
                                          ?.validated &&
                                          infoMission.user_assigned_infos
                                            .tempworker?.hostIdentity
                                            ?.validated !== '0' && (
                                          <IdentityFileItem
                                            title={hostIdentityTitle}
                                            openDocument={openDocument}
                                            id="hostIdentity"
                                            validatedType={
                                              hostIdentityValidatedType
                                            }
                                            details={[]}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <EmptyStateMission type="profil" />
                                )}
                              </div>
                              {infoMission.user_assigned ? (
                                <>
                                  <Divider />
                                  <ContractsAndAmendmentsList
                                    postIt={
                                      <PostIt
                                        note={infoMission.company?.post_it}
                                        companyName={
                                          infoMission.company?.name_commercial ??
                                          infoMission.company?.reason_social
                                        }
                                      />
                                    }
                                    mission={infoMission}
                                    onGenerateContract={openContractGeneration}
                                    contracts={infoMission?.contract_tempworker || []}
                                    companyContracts={infoMission?.contract_company || []}
                                    amendments={infoMission?.amendments || []}
                                    contractHistories={infoMission?.contractHistories || []}
                                    missionId={infoMission._id}
                                    hasUserAssigned={!!infoMission.user_assigned}
                                    canActOnAmendments={isSuperAdmin(userData)}
                                    onPreviewContract={openContractPreview}
                                    onAmendmentAction={openAmendmentAction}
                                    loadingContractGeneration={loadingContractGeneration}
                                    loadingSignatureMissionId={loadingSignatureMissionId}
                                    generationContractSubmit={generationContractSubmit}
                                    regenerateAmendmentSubmit={regenerateAmendmentSubmit}
                                    updateContractSubmit={updateContractSubmit}
                                    sendSignatureMissionSubmit={sendSignatureMissionSubmit}
                                    deletedContractHistory={deletedContractHistory}
                                  />
                                </>
                              ) : (
                                // No contract section for it to sit under until
                                // somebody is assigned.
                                <PostIt
                                  note={infoMission.company?.post_it}
                                  companyName={
                                    infoMission.company?.name_commercial ??
                                    infoMission.company?.reason_social
                                  }
                                />
                              )}
                            </div>
                          )}
                          {missionPage === 3 && (
                            <>
                              <div
                                className={
                                  missionDetailPageClassName
                                }
                              >
                                <div
                                  className={
                                    'flex flex-1 flex-col gap-6 text-sm'
                                  }
                                >
                                  {(role === 500 || role === 515) && (
                                    <>
                                      <div
                                        className={
                                          'flex gap-2 justify-between items-center'
                                        }
                                      >
                                        <div className={''}>
                                          Modifier le détail de la mission
                                        </div>
                                        {!modifyMissionDetail && (
                                          <div className={''}>
                                            <Button
                                              size={'SM'}
                                              color={'SECONDARY'}
                                              onClick={() =>
                                                setModifyMissionDetail(true)
                                              }
                                            >
                                              <div className={'flex gap-1'}>
                                                <div>
                                                  <Pencil
                                                    wh={20}
                                                    color={'#374151'}
                                                  />
                                                </div>
                                                Modifier
                                              </div>
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      <Divider />
                                    </>
                                  )}
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Entreprise
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {infoMission.company?.name_commercial}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Qualification
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <div className={''}>
                                          <StyledInput
                                            type={'selectMultipleRadio'}
                                            value={
                                              qualifications.filter(
                                                (qualification) =>
                                                  qualification.checked === true
                                              ).length > 0
                                                ? qualifications
                                                    .filter(
                                                      (qualification) =>
                                                        qualification.checked ===
                                                        true
                                                    )
                                                    .map(
                                                      (qualification) =>
                                                        qualification.name
                                                    )
                                                    .join(', ')
                                                : 'Qualification'
                                            }
                                            list={qualifications.map(
                                              (qualification) => {
                                                return {
                                                  value: qualification._id,
                                                  label: qualification.name,
                                                  checked:
                                                    qualification.checked,
                                                };
                                              }
                                            )}
                                            onChange={(updatedQualification) =>
                                              updateQualification(
                                                updatedQualification
                                              )
                                            }
                                            isSearchable
                                          />
                                        </div>
                                      ) : (
                                        infoMission.title
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Période
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <div className={'flex gap-2 text-sm'}>
                                          <div className={'w-full'}>
                                            <div>
                                              <label className="text-gray-500 flex flex-col gap-1">
                                                <div>Date de début</div>
                                                <DatePicker
                                                  dateFormat="dd/MM/yyyy"
                                                  selected={
                                                    modifyMissionDetailData
                                                      .period.start
                                                  }
                                                  onChange={(date) =>
                                                    setModifyMissionDetailData(
                                                      (prevData) => ({
                                                        ...prevData,
                                                        period: {
                                                          ...prevData.period,
                                                          start: date,
                                                        },
                                                      })
                                                    )
                                                  }
                                                  locale="fr-FR"
                                                  peekNextMonth
                                                  showMonthDropdown
                                                  showYearDropdown
                                                  dropdownMode="select"
                                                  popperPlacement="bottom"
                                                  className="text-sm w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center"
                                                />
                                              </label>
                                            </div>
                                          </div>
                                          <div className={'w-full'}>
                                            <div>
                                              <label className="text-gray-500 flex flex-col gap-1">
                                                <div>Date de fin</div>
                                                <DatePicker
                                                  dateFormat="dd/MM/yyyy"
                                                  selected={
                                                    modifyMissionDetailData
                                                      .period.end
                                                  }
                                                  onChange={(date) =>
                                                    setModifyMissionDetailData(
                                                      (prevData) => ({
                                                        ...prevData,
                                                        period: {
                                                          ...prevData.period,
                                                          end: date,
                                                        },
                                                      })
                                                    )
                                                  }
                                                  locale="fr-FR"
                                                  peekNextMonth
                                                  showMonthDropdown
                                                  showYearDropdown
                                                  dropdownMode="select"
                                                  popperPlacement="bottom"
                                                  className="text-sm w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center"
                                                />
                                              </label>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className={'flex gap-1'}>
                                          <div className={''}>
                                            {dateWordShort(
                                              infoMission.duration?.datestart
                                            )}
                                          </div>
                                          <div className={''}>{'>'}</div>
                                          <div className={''}>
                                            {dateWordShort(
                                              infoMission.duration?.dateend
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {modifyMissionDetail ? (
                                    <Schedule
                                      mission={infoMission}
                                      dateStart={
                                        modifyMissionDetailData.period.start
                                      }
                                      dateEnd={
                                        modifyMissionDetailData.period.end
                                      }
                                      schedule={
                                        modifyMissionDetailData.schedule
                                      }
                                      isEditable
                                    />
                                  ) : (
                                    <Schedule
                                      mission={infoMission}
                                      dateStart={infoMission.duration.datestart}
                                      dateEnd={infoMission.duration.dateend}
                                      schedule={infoMission.schedules}
                                    />
                                  )}
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Rémunération
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <Salary
                                          salary={
                                            modifyMissionDetailData.salary
                                          }
                                          previousSalary={
                                            infoMission.salary?.wage_per_hour
                                          }
                                          hasContract={
                                            infoMission.contract_tempworker?.some(
                                              (c) => c.id_bestt
                                            ) ?? false
                                          }
                                          onValidityChange={setIsSalaryValid}
                                          onChange={(value) => {
                                            setModifyMissionDetailData(
                                              (prevData) => ({
                                                ...prevData,
                                                salary: value,
                                              })
                                            );
                                          }}
                                        />
                                      ) : (
                                        infoMission.salary.wage_per_hour + '€/h'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Adresse
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <AddressGeoApi
                                          label={'Adresse'}
                                          addressGeoValue={
                                            setAddressMissionDetail
                                          }
                                          addressValue={addressMissionDetail}
                                          isAddressCompany={
                                            infoMission.company.location
                                          }
                                          page={'adminMissionDetail'}
                                        />
                                      ) : (
                                        infoMission.address.street +
                                        ' ' +
                                        infoMission.address.postcode +
                                        ' ' +
                                        infoMission.address.city +
                                        ', ' +
                                        infoMission.address.department
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Motif
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <div
                                          className={
                                            'border rounded-lg px-2 py-3 flex justify-between text-sm text-gray-700'
                                          }
                                        >
                                          <div
                                            className={'flex flex-col gap-1'}
                                          >
                                            <div className={'font-bold'}>
                                              {
                                                modifyMissionDetailData.reason
                                                  .reason
                                              }
                                            </div>
                                            <div className={''}>
                                              {(modifyMissionDetailData.reason
                                                .proof_firstname ||
                                                modifyMissionDetailData.reason
                                                  .proof_lastname) &&
                                                modifyMissionDetailData.reason
                                                  .proof_firstname +
                                                  ' ' +
                                                  modifyMissionDetailData.reason
                                                    .proof_lastname +
                                                  ' - '}{' '}
                                              {
                                                modifyMissionDetailData.reason
                                                  .proof
                                              }
                                            </div>
                                          </div>
                                          <div className={''}>
                                            <Button
                                              size={'SMI'}
                                              color={'SECONDARY'}
                                              onClick={() =>
                                                editMissionDetailReason()
                                              }
                                            >
                                              <Pencil
                                                wh={20}
                                                color={'#374151'}
                                              />
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          {infoMission.reason_detail.reason} :{' '}
                                          {(infoMission.reason_detail
                                            .proof_firstname ||
                                            infoMission.reason_detail
                                              .proof_lastname) &&
                                            infoMission.reason_detail
                                              .proof_firstname +
                                              ' ' +
                                              infoMission.reason_detail
                                                .proof_lastname +
                                              ' - '}{' '}
                                          {infoMission.reason_detail.proof}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Permis
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <StyledInput
                                          type={'selectMultipleCheckbox'}
                                          isSearchable
                                          value={
                                            licences.filter(
                                              (licence) =>
                                                licence.checked === true
                                            ).length > 0
                                              ? licences
                                                  .filter(
                                                    (licence) =>
                                                      licence.checked === true
                                                  )
                                                  .map(
                                                    (licence) => licence.name
                                                  )
                                                  .join(', ')
                                              : '-'
                                          }
                                          list={licences.map((licence) => {
                                            return {
                                              value: licence.name,
                                              label: licence.name,
                                              checked: licence.checked,
                                            };
                                          })}
                                          onChange={(updatedLicence) =>
                                            updateLicence(updatedLicence)
                                          }
                                        />
                                      ) : infoMission.licences.length > 0 ? (
                                        infoMission.licences.join(', ')
                                      ) : (
                                        'Aucun'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Matériel
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <StyledInput
                                          type={'selectMultipleCheckbox'}
                                          isSearchable
                                          value={
                                            tools.filter(
                                              (tool) => tool.checked === true
                                            ).length > 0
                                              ? tools
                                                  .filter(
                                                    (tool) =>
                                                      tool.checked === true
                                                  )
                                                  .map((tool) => tool.name)
                                                  .join(', ')
                                              : '-'
                                          }
                                          list={tools.map((tool) => {
                                            return {
                                              value: tool.name,
                                              label: tool.name,
                                              checked: tool.checked,
                                            };
                                          })}
                                          onChange={(updatedTool) =>
                                            updateTool(updatedTool)
                                          }
                                        />
                                      ) : infoMission.tools.length > 0 ? (
                                        infoMission.tools.join(', ')
                                      ) : (
                                        'Aucun'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Langue
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <StyledInput
                                          type={'selectMultipleCheckbox'}
                                          isSearchable
                                          value={
                                            lang.filter(
                                              (lang) => lang.checked === true
                                            ).length > 0
                                              ? lang
                                                  .filter(
                                                    (lang) =>
                                                      lang.checked === true
                                                  )
                                                  .map((lang) => lang.name)
                                                  .join(', ')
                                              : '-'
                                          }
                                          list={lang.map((lang) => {
                                            return {
                                              value: lang.name,
                                              label: lang.name,
                                              checked: lang.checked,
                                            };
                                          })}
                                          onChange={(updatedLanguage) =>
                                            updateLanguage(updatedLanguage)
                                          }
                                        />
                                      ) : infoMission.language.length > 0 ? (
                                        infoMission.language.join(', ')
                                      ) : (
                                        'Aucune'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Référence
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <StyledInput
                                          type={'text'}
                                          value={
                                            modifyMissionDetailData.reference
                                          }
                                          onChange={(e) => {
                                            setModifyMissionDetailData(
                                              (prevData) => ({
                                                ...prevData,
                                                reference: e.target.value,
                                              })
                                            );
                                          }}
                                        />
                                      ) : (
                                        infoMission.reference || 'Aucune'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Informations pour le candidat
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <StyledInput
                                          type={'textarea'}
                                          value={modifyMissionDetailData.other}
                                          onChange={(e) => {
                                            setModifyMissionDetailData(
                                              (prevData) => ({
                                                ...prevData,
                                                other: e.target.value,
                                              })
                                            );
                                          }}
                                        />
                                      ) : (
                                        infoMission.other || 'Aucune'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                  <div className={'flex gap-1'}>
                                    <div className={'w-1/2 font-medium'}>
                                      Informations pour l'agence
                                    </div>
                                    <div className={'w-1/2 text-gray-700'}>
                                      {modifyMissionDetail ? (
                                        <StyledInput
                                          type={'textarea'}
                                          value={
                                            modifyMissionDetailData.other_interim
                                          }
                                          onChange={(e) => {
                                            setModifyMissionDetailData(
                                              (prevData) => ({
                                                ...prevData,
                                                other_interim: e.target.value,
                                              })
                                            );
                                          }}
                                        />
                                      ) : (
                                        infoMission.other_interim || 'Aucune'
                                      )}
                                    </div>
                                  </div>
                                  <Divider />
                                </div>
                              </div>
                              {modifyMissionDetail && (
                                <div
                                  className={
                                    'flex items-center justify-end px-6 py-0 rounded-b gap-2'
                                  }
                                >
                                  <Button
                                    size={'SM'}
                                    color={'SECONDARY'}
                                    onClick={() =>
                                      setModifyMissionDetail(false)
                                    }
                                  >
                                    Annuler
                                  </Button>
                                  {isChangesModifyMissionDetail ? (
                                    <Button
                                      size={'SM'}
                                      color={'PRIMARY'}
                                      onClick={() =>
                                        submitModifyMissionDetail()
                                      }
                                    >
                                      Confirmer
                                    </Button>
                                  ) : (
                                    <Button size={'SM'} color={'DISABLED'}>
                                      Confirmer
                                    </Button>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {missionPage === 4 && (
                            <MissionActivityTab
                              missionId={missionId}
                              refreshNonce={activityRefreshNonce}
                            />
                          )}
                        </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>Pas de données</>
          )}
        </>
      )}
    </>
  );
};

export default AdminMissionDetails;